import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import clsx from 'clsx';
import { KTSVG } from '../../../../_metronic/helpers';
import { Timestamp, doc, serverTimestamp, updateDoc, collection, onSnapshot, query, limit, addDoc, getDocs, deleteDoc, orderBy } from 'firebase/firestore';
import { db, app } from '../../../../firebase';
import { useEmailPanel, PanelEmailData } from '../contexts/EmailPanelContext'; // Ensure correct path
import { authFetch } from '../../../utils/cloudFunctionsAuth';
import { getGraphFlagWrite } from '../utils/emailFlagState';
import { warmGraphEmailBodyCache } from '../utils/graphEmailBodyCache';
import { normalizeEmailFolder, isOutlookFiled } from '../utils/emailFolder';
import NewJobModal from '../../job-summary/NewJobModal';
import JobWorkspaceModal from '../../job-summary/JobWorkspaceModal';
import { Job } from '../../job-summary/Interfaces';

const FUNCTIONS_BASE = 'https://us-central1-web-construct-app.cloudfunctions.net';

// Saved Search interface
interface SavedSearch {
  id: string;
  query: string;
  name?: string;
  emailIds: string[];
  resultCount: number;
  createdAt: Date;
}

// Job interface for linking
interface JobForLinking {
  id: string;
  jobLabel: string;
  status: string;
  customerId: string[];
}

// Customer interface for email matching
interface CustomerForLinking {
  id: string;
  name: string;
  email: string;
  company?: string;
}

const ACTIVE_JOB_STATUSES = ['Open', 'Active', 'Pending', 'Awaiting Client Response'];

// Define the structure for email data as it comes from Firestore and is used in the feed
// This should match the structure used in HeaderWrapper previously
export interface FeedEmailData extends PanelEmailData { // Extends PanelEmailData for compatibility with openEmailPanel
    id: string;          // Firestore Document ID
    emailId: string;     // Usually same as id
    title: string;
    subject: string;
    message: string;     // Plain text snippet
    text: string;        // Full plain text
    html?: string | null; // Full HTML
    createdAt: Date;     // Converted from Firestore Timestamp
    date: Timestamp;     // Original Firestore Timestamp
    dismissed: boolean;
    deleted?: boolean;   // For filtering
    purged?: boolean;    // Deleted forever from Outlook's Deleted Items: the doc is kept for history but never listed
    pinned?: boolean;    // Legacy field: Graph mail uses this for Outlook follow-up flags
    sender: string;
    emailType: 'inbox' | 'sent';
    // `folder` (logical folder, may be missing on legacy docs) is inherited from
    // PanelEmailData rather than redeclared here, so the two can never drift apart
    // again as folder values are added (archive/filed). Read it through
    // normalizeEmailFolder (../utils/emailFolder), never directly.
    // Outlook folder mail was filed into - only set for folder 'archive' | 'filed'.
    outlookFolderId?: string;
    outlookFolderName?: string; // immediate folder display name, e.g. "Trinity"
    outlookFolderPath?: string; // full path, e.g. "Personal/Trinity"
    from: { address: string; name?: string; text?:string }[];
    to: string | string[]; // Override to ensure it's always defined
    cc?: string | string[]; // CC recipients
    bcc?: string | string[]; // BCC recipients
    attachments?: any[]; // Define a more specific type if possible
    customerId?: string | null;
    // Classification helpers
    workRelated?: boolean | null;
    quarantined?: boolean | null;
    senderEmail?: string | undefined;
    source?: 'imap' | 'graph';
    mailbox?: string;
    graph?: { id?: string | null; conversationId?: string | null; flagStatus?: string | null };
    virtualFolderId?: string; // ID of custom folder if rule matches
    // Work Hub fields
    dismissedFromWorkHub?: boolean;
    linkedJobId?: string | null;
    linkedJobLabel?: string | null;
    // Snooze: ms-epoch the email should resurface (client-side filtered).
    snoozedUntil?: number | null;
    // Waiting-on-them ledger opt-out (owner dismissed this sent email).
    waitingDismissed?: boolean;
}

interface EmailFeedContentProps {
    // theme: 'light' | 'dark' | 'night'; // Panel will pass this if needed for specific styling
}

const EmailFeedContent: React.FC<EmailFeedContentProps> = (/* { theme } */) => {
  const {
    openEmailPanel,
    setEmailPanelView,
    preLoadedEmails,        // Use from context
    isLoadingInitialEmails, // Use from context
    loadEmailsForDateRange,
    loadEmailsByIds,        // Load specific emails by ID (for search results)
    isLoadingOlderEmails,   // Use from context for fetching older emails
    hasMoreOlderEmails,     // Use from context
    loadOlderEmails,        // Use from context
    userDrafts, // Access user drafts count for the tab
    customFolders,
    createCustomFolder,
    addSenderToFolder,
    removeSenderFromFolder,
    renameCustomFolder,
    setCustomFolderParent,
    deleteCustomFolder,
  } = useEmailPanel();

  const [searchTerm, setSearchTerm] = useState("");
  // Outlook-like: search starts in the last 30 days; user can tap "Search more" to expand in 30-day increments.
  const [searchLookbackDays, setSearchLookbackDays] = useState<number>(30);

  const [showDismissAllConfirm, setShowDismissAllConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [emailToDelete, setEmailToDelete] = useState<FeedEmailData | null>(null);
  const [theme, setThemeState] = useState('light'); // Local theme state for the component itself
  const [activeTab, setActiveTab] = useState<string>('inbox');
  const [isLoadingRange, setIsLoadingRange] = useState<boolean>(false);
  const [isSyncingGraph, setIsSyncingGraph] = useState<boolean>(false);
  const [isSyncingMailbox, setIsSyncingMailbox] = useState<boolean>(false); // Manual mailbox sync
  const [syncStatus, setSyncStatus] = useState<string | null>(null); // Status message for sync progress
  const [displayCount, setDisplayCount] = useState<number>(30); // Pagination: only render 30 at a time initially
  const [pendingByEmailId, setPendingByEmailId] = useState<Record<string, { pin?: boolean; read?: boolean; deleting?: boolean; junk?: boolean }>>({});
  const hoverPrefetchTimersRef = React.useRef<Record<string, number>>({});

  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderIsGroup, setNewFolderIsGroup] = useState<boolean>(false);
  const [newFolderParentId, setNewFolderParentId] = useState<string>(''); // '' = no group
  const [movingEmail, setMovingEmail] = useState<FeedEmailData | null>(null);

  // Folder management (rename/delete)
  const [showEditFolderModal, setShowEditFolderModal] = useState(false);
  const [folderToEdit, setFolderToEdit] = useState<{ id: string; name: string; isGroup?: boolean; parentId?: string | null } | null>(null);
  const [editFolderName, setEditFolderName] = useState('');
  const [editFolderParentId, setEditFolderParentId] = useState<string>(''); // '' = no group
  const [showDeleteFolderConfirm, setShowDeleteFolderConfirm] = useState(false);
  const [folderToDelete, setFolderToDelete] = useState<{ id: string; name: string; isGroup?: boolean } | null>(null);
  // Bug 5: track folder creation errors so we can show UI feedback
  const [folderCreateError, setFolderCreateError] = useState<string | null>(null);

  // UX 1: Undo toast for destructive actions
  const [undoToast, setUndoToast] = useState<{
    message: string;
    onUndo: () => void;
    timerId: ReturnType<typeof setTimeout>;
  } | null>(null);

  const dismissUndoToast = useCallback(() => {
    setUndoToast((prev) => {
      if (prev) clearTimeout(prev.timerId);
      return null;
    });
  }, []);

  const groupFolders = useMemo(() => customFolders.filter((f) => f.isGroup === true), [customFolders]);
  const leafFolders = useMemo(() => customFolders.filter((f) => f.isGroup !== true), [customFolders]);

  const leafByGroupId = useMemo(() => {
    const map: Record<string, typeof leafFolders> = {};
    leafFolders.forEach((f) => {
      const pid = f.parentId || '';
      if (!pid) return;
      if (!map[pid]) map[pid] = [];
      map[pid].push(f);
    });
    Object.keys(map).forEach((k) => map[k].sort((a, b) => (a.name || '').localeCompare(b.name || '')));
    return map;
  }, [leafFolders]);

  const ungroupedLeafFolders = useMemo(() => {
    const groupIds = new Set(groupFolders.map((g) => g.id));
    return leafFolders
      .filter((f) => {
        const pid = f.parentId || '';
        if (!pid) return true;
        return !groupIds.has(pid);
      })
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [leafFolders, groupFolders]);

  const [expandedGroupIds, setExpandedGroupIds] = useState<Record<string, boolean>>(() => {
    try {
      const raw = localStorage.getItem('emailFeed.expandedGroups');
      return raw ? (JSON.parse(raw) as Record<string, boolean>) : {};
    } catch {
      return {};
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem('emailFeed.expandedGroups', JSON.stringify(expandedGroupIds));
    } catch {}
  }, [expandedGroupIds]);

  const toggleGroupExpanded = (groupId: string) => {
    setExpandedGroupIds((prev) => ({ ...prev, [groupId]: !(prev[groupId] ?? true) }));
  };
  const [selectedMailbox, setSelectedMailbox] = useState<string>('all'); // 'all' or specific mailbox email
  const lastGraphSearchKeyRef = React.useRef<string>('');
  
  // Rate limiting and in-flight tracking for Graph searches
  const isGraphSearchInFlightRef = React.useRef<boolean>(false);
  const lastGraphSearchTimeRef = React.useRef<number>(0);
  const GRAPH_SEARCH_MIN_INTERVAL_MS = 2000; // Minimum 2 seconds between searches
  const [searchCooldownRemaining, setSearchCooldownRemaining] = useState<number>(0);

  // Saved Searches state
  const [savedSearches, setSavedSearches] = useState<SavedSearch[]>([]);
  const [isSavingSearch, setIsSavingSearch] = useState(false);
  const [isLoadingSavedSearch, setIsLoadingSavedSearch] = useState(false);
  const [lastSearchResultIds, setLastSearchResultIds] = useState<string[]>([]); // Track IDs from last Graph search
  const [showSaveSearchModal, setShowSaveSearchModal] = useState(false);
  const [saveSearchName, setSaveSearchName] = useState('');

  // UX 4: saved search inline rename
  const [renamingSearchId, setRenamingSearchId] = useState<string | null>(null);
  const [renameSearchValue, setRenameSearchValue] = useState('');

  // Algolia instant search state
  const [isSearchingAlgolia, setIsSearchingAlgolia] = useState(false);
  const [algoliaSearchLatency, setAlgoliaSearchLatency] = useState<number | null>(null);
  const algoliaSearchDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const lastAlgoliaQueryRef = useRef<string>(''); // Prevent duplicate searches for same query
  // Bug 3: store the post-search status-clear timeout so it can be cancelled on unmount
  const algoliaStatusClearTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Last synced timestamp
  const [lastSyncedAt, setLastSyncedAt] = useState<Date | null>(null);

  // Link to Job state
  const [allJobs, setAllJobs] = useState<JobForLinking[]>([]);
  const [allCustomers, setAllCustomers] = useState<CustomerForLinking[]>([]);
  const [linkingEmailId, setLinkingEmailId] = useState<string | null>(null);
  
  // Manual Link Modal state
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkModalEmail, setLinkModalEmail] = useState<FeedEmailData | null>(null);
  const [linkModalCustomerSearch, setLinkModalCustomerSearch] = useState('');
  const [linkModalSelectedCustomer, setLinkModalSelectedCustomer] = useState<CustomerForLinking | null>(null);

  // Create Job from Email Modal state
  const [showNewJobModal, setShowNewJobModal] = useState(false);
  const [newJobEmailContext, setNewJobEmailContext] = useState<{
    email: FeedEmailData;
    customerId: string | null;
  } | null>(null);
  
  // Job Workspace Modal state (opens after creating new job from email)
  const [showJobWorkspaceModal, setShowJobWorkspaceModal] = useState(false);
  const [selectedJobForWorkspace, setSelectedJobForWorkspace] = useState<Job | null>(null);

  // Helper to format "time ago" for last synced display
  const formatTimeAgo = (date: Date | null): string => {
    if (!date) return '';
    const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
    if (seconds < 60) return 'just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes} min ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hr ago`;
    const days = Math.floor(hours / 24);
    return `${days} day${days > 1 ? 's' : ''} ago`;
  };

  // Subscribe to jobs for "Link to Job" feature
  useEffect(() => {
    const qRef = query(collection(db, 'jobs'), limit(500));
    const unsub = onSnapshot(qRef, (snap) => {
      const docs: JobForLinking[] = snap.docs.map((d) => ({
        id: d.id,
        jobLabel: d.data().jobLabel || `Job #${d.id.slice(0, 8)}`,
        status: d.data().status || 'Unknown',
        customerId: d.data().customerId || [],
      }));
      setAllJobs(docs);
    }, (err) => console.error('[EmailFeed] jobs subscription error', err));
    return () => unsub();
  }, []);

  // Subscribe to customers for email→customer matching
  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'customers'), (snap) => {
      const docs: CustomerForLinking[] = snap.docs.map((d) => ({
        id: d.id,
        name: d.data().name || 'Unknown',
        email: (d.data().email || '').toLowerCase().trim(),
        company: d.data().company || '',
      }));
      setAllCustomers(docs);
    }, (err) => console.error('[EmailFeed] customers subscription error', err));
    return () => unsub();
  }, []);

  // Subscribe to saved searches
  useEffect(() => {
    const q = query(collection(db, 'savedSearches'), orderBy('createdAt', 'desc'), limit(20));
    const unsub = onSnapshot(q, (snap) => {
      const docs: SavedSearch[] = snap.docs.map((d) => ({
        id: d.id,
        query: d.data().query || '',
        name: d.data().name,
        emailIds: d.data().emailIds || [],
        resultCount: d.data().resultCount || 0,
        createdAt: d.data().createdAt?.toDate?.() || new Date(),
      }));
      setSavedSearches(docs);
    }, (err) => console.error('[EmailFeed] saved searches subscription error', err));
    return () => unsub();
  }, []);

  // Save current search results
  const handleSaveSearch = useCallback(async () => {
    if (lastSearchResultIds.length === 0 || !searchTerm?.trim()) return;
    
    setIsSavingSearch(true);
    try {
      await addDoc(collection(db, 'savedSearches'), {
        query: searchTerm.trim(),
        name: saveSearchName.trim() || searchTerm.trim(),
        emailIds: lastSearchResultIds,
        resultCount: lastSearchResultIds.length,
        createdAt: serverTimestamp(),
      });
      setSyncStatus(`Saved "${saveSearchName || searchTerm}" (${lastSearchResultIds.length} emails)`);
      setShowSaveSearchModal(false);
      setSaveSearchName('');
      setTimeout(() => setSyncStatus(null), 3000);
    } catch (err) {
      console.error('[EmailFeed] Failed to save search:', err);
      setSyncStatus('Failed to save search');
      setTimeout(() => setSyncStatus(null), 3000);
    } finally {
      setIsSavingSearch(false);
    }
  }, [lastSearchResultIds, searchTerm, saveSearchName]);

  // Load a saved search (instantly loads emails by ID)
  const handleLoadSavedSearch = useCallback(async (savedSearch: SavedSearch) => {
    if (savedSearch.emailIds.length === 0) return;
    
    setIsLoadingSavedSearch(true);
    setSearchTerm(savedSearch.query); // Show the search term
    setSyncStatus(`Loading "${savedSearch.name || savedSearch.query}"...`);
    
    try {
      // Use the loadEmailsByIds function from context - this is instant since emails are in Firestore
      await loadEmailsByIds(savedSearch.emailIds);
      setSyncStatus(`Loaded ${savedSearch.resultCount} emails`);
      setTimeout(() => setSyncStatus(null), 3000);
    } catch (err) {
      console.error('[EmailFeed] Failed to load saved search:', err);
      setSyncStatus('Failed to load saved search');
      setTimeout(() => setSyncStatus(null), 3000);
    } finally {
      setIsLoadingSavedSearch(false);
    }
  }, [loadEmailsByIds]);

  // Delete a saved search
  const handleDeleteSavedSearch = useCallback(async (searchId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering the load
    try {
      await deleteDoc(doc(db, 'savedSearches', searchId));
      setSyncStatus('Search deleted');
      setTimeout(() => setSyncStatus(null), 2000);
    } catch (err) {
      console.error('[EmailFeed] Failed to delete saved search:', err);
    }
  }, []);

  // UX 4: commit an inline rename of a saved search
  const handleCommitRenameSearch = useCallback(async (searchId: string) => {
    const newName = renameSearchValue.trim();
    if (!newName) {
      setRenamingSearchId(null);
      return;
    }
    try {
      await updateDoc(doc(db, 'savedSearches', searchId), { name: newName });
    } catch (err) {
      console.error('[EmailFeed] Failed to rename saved search:', err);
    } finally {
      setRenamingSearchId(null);
      setRenameSearchValue('');
    }
  }, [renameSearchValue]);

  // Active jobs only
  const activeJobs = useMemo(() => {
    return allJobs.filter((j) => ACTIVE_JOB_STATUSES.includes(j.status));
  }, [allJobs]);

  // Customer email → ID index
  const customerIdByEmail = useMemo(() => {
    const index: Record<string, string> = {};
    allCustomers.forEach((c) => {
      if (c.email) index[c.email] = c.id;
    });
    return index;
  }, [allCustomers]);

  // Get customer name by ID
  const getCustomerName = (customerId: string): string => {
    return allCustomers.find((c) => c.id === customerId)?.name || 'Unknown';
  };

  // Extract recipient emails from an email (for outbound matching)
  const extractRecipientEmails = (email: FeedEmailData): string[] => {
    const addresses: string[] = [];
    const addAddresses = (field: any) => {
      if (!field) return;
      if (Array.isArray(field)) {
        field.forEach((item) => {
          if (typeof item === 'string') addresses.push(item.toLowerCase().trim());
          else if (item?.address) addresses.push(item.address.toLowerCase().trim());
        });
      } else if (typeof field === 'string') {
        addresses.push(field.toLowerCase().trim());
      } else if (field?.address) {
        addresses.push(field.address.toLowerCase().trim());
      }
    };
    addAddresses(email.to);
    addAddresses(email.cc);
    addAddresses(email.bcc);
    return addresses;
  };

  // Get the "true customer" for an email based on direction
  const getTrueCustomerId = (email: FeedEmailData): string | null => {
    const isOutbound = email.folder === 'sent' || email.emailType === 'sent';
    
    if (isOutbound) {
      // For sent emails: the customer is the RECIPIENT
      const recipientEmails = extractRecipientEmails(email);
      for (const addr of recipientEmails) {
        const matchedCid = customerIdByEmail[addr];
        if (matchedCid) return matchedCid;
      }
    } else {
      // For received emails: the customer is the SENDER
      if (email.customerId && typeof email.customerId === 'string') return email.customerId;
      const senderAddr = email.from?.[0]?.address?.toLowerCase().trim();
      if (senderAddr) {
        const matchedCid = customerIdByEmail[senderAddr];
        if (matchedCid) return matchedCid;
      }
    }
    return null;
  };

  // Get active jobs for a customer
  const getJobsForCustomer = (customerId: string): JobForLinking[] => {
    return activeJobs.filter((j) => j.customerId.includes(customerId));
  };

  // Handle linking email to job
  const handleLinkToJob = async (emailId: string, jobId: string, jobLabel: string) => {
    setLinkingEmailId(emailId);
    try {
      const resp = await authFetch('https://us-central1-web-construct-app.cloudfunctions.net/linkEmailToJob', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ emailId, jobId, jobLabel }),
      });
      if (!resp.ok) {
        console.error('[EmailFeed] linkEmailToJob failed:', await resp.text());
      } else {
        // Close modal on success
        setShowLinkModal(false);
        setLinkModalEmail(null);
        setLinkModalSelectedCustomer(null);
        setLinkModalCustomerSearch('');
      }
    } catch (err) {
      console.error('[EmailFeed] linkEmailToJob error:', err);
    } finally {
      setLinkingEmailId(null);
    }
  };

  // Open manual link modal
  const openLinkModal = (email: FeedEmailData) => {
    setLinkModalEmail(email);
    setLinkModalCustomerSearch('');
    setLinkModalSelectedCustomer(null);
    setShowLinkModal(true);
  };

  // Open Create Job modal with auto-detected customer and email subject as description
  const openCreateJobModal = (email: FeedEmailData, preselectedCustomerId?: string | null) => {
    // Auto-detect customer from email if not provided
    const customerId = preselectedCustomerId || getTrueCustomerId(email);
    
    setNewJobEmailContext({
      email,
      customerId: customerId || null
    });
    setShowNewJobModal(true);
  };

  // Handle when a new job is created from the NewJobModal - link the email to it and open workspace
  const handleNewJobCreated = async (job: Job) => {
    if (newJobEmailContext?.email) {
      const emailId = newJobEmailContext.email.emailId || newJobEmailContext.email.id;
      await handleLinkToJob(emailId, job.id, job.jobLabel);
      setSyncStatus(`Created job and linked email: ${job.jobLabel}`);
      setTimeout(() => setSyncStatus(null), 3000);
    }
    setNewJobEmailContext(null);
    
    // Open the Job Workspace Modal with the newly created job
    setSelectedJobForWorkspace(job);
    setShowJobWorkspaceModal(true);
  };

  const openEditFolderModal = (folder: { id: string; name: string }) => {
    const full = customFolders.find((f) => f.id === folder.id);
    setFolderToEdit({ id: folder.id, name: folder.name, isGroup: full?.isGroup, parentId: full?.parentId ?? null });
    setEditFolderName(folder.name || '');
    setEditFolderParentId(full?.parentId ? String(full.parentId) : '');
    setShowEditFolderModal(true);
  };

  const openDeleteFolderModal = (folder: { id: string; name: string }) => {
    const full = customFolders.find((f) => f.id === folder.id);
    // Prevent deleting a group that still contains child folders (Option A safety).
    if (full?.isGroup === true) {
      const children = leafByGroupId[full.id] || [];
      if (children.length > 0) {
        setSyncStatus(`Delete "${full.name}" subfolders first`);
        setTimeout(() => setSyncStatus(null), 2500);
        return;
      }
    }
    setFolderToDelete({ id: folder.id, name: folder.name, isGroup: full?.isGroup });
    setShowDeleteFolderConfirm(true);
  };

  // Filtered customers for modal search
  const filteredCustomersForModal = useMemo(() => {
    if (!linkModalCustomerSearch.trim()) return allCustomers.slice(0, 10);
    const search = linkModalCustomerSearch.toLowerCase();
    return allCustomers
      .filter((c) => c.name.toLowerCase().includes(search) || c.email.includes(search))
      .slice(0, 10);
  }, [allCustomers, linkModalCustomerSearch]);

  // ALL jobs for selected customer in modal (no status filter)
  const jobsForModalCustomer = useMemo(() => {
    if (!linkModalSelectedCustomer) return [];
    return allJobs.filter((j) => j.customerId.includes(linkModalSelectedCustomer.id));
  }, [allJobs, linkModalSelectedCustomer]);

  const fetchWithTimeout = async (url: string, options: RequestInit, timeoutMs: number) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const response = await authFetch(url, { ...options, signal: controller.signal });
      clearTimeout(timeoutId);
      return response;
    } catch (err: any) {
      clearTimeout(timeoutId);
      if (err?.name === 'AbortError') throw new Error('Request timed out');
      throw err;
    }
  };

  // Algolia instant search - lightning fast!
  const searchEmailsAlgolia = useCallback(async (searchQuery: string) => {
    const trimmedQuery = searchQuery?.trim() || '';
    
    if (trimmedQuery.length < 2) {
      setLastSearchResultIds([]);
      lastAlgoliaQueryRef.current = '';
      return;
    }

    // Prevent duplicate searches for the same query
    if (lastAlgoliaQueryRef.current === trimmedQuery) {
      return;
    }
    lastAlgoliaQueryRef.current = trimmedQuery;

    setIsSearchingAlgolia(true);
    setSyncStatus('Searching...');
    
    try {
      const resp = await authFetch(`${FUNCTIONS_BASE}/searchEmailsAlgoliaHttp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: trimmedQuery, limit: 100 }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const result = await resp.json();
      const data = (result.data || result) as any;
      
      if (data?.results && data.results.length > 0) {
        const emailIds = data.results.map((r: any) => r.id);
        setLastSearchResultIds(emailIds);
        
        // Load the emails from Firestore
        await loadEmailsByIds(emailIds);
        
        const latencyMs = data.latencyMs || 0;
        setAlgoliaSearchLatency(latencyMs);
        setSyncStatus(`Found ${data.totalHits} emails in ${latencyMs}ms`);
        
        console.log('[Algolia Search] Results:', {
          query: trimmedQuery,
          totalHits: data.totalHits,
          returned: data.results.length,
          latencyMs
        });
      } else {
        setLastSearchResultIds([]);
        setSyncStatus('No matches found');
        console.log('[Algolia Search] No results for:', trimmedQuery);
      }
    } catch (error: any) {
      console.error('[Algolia Search] Error:', error);
      // Don't show error to user - just fall back to local search
      setSyncStatus(null);
      // Reset so user can retry
      lastAlgoliaQueryRef.current = '';
    } finally {
      setIsSearchingAlgolia(false);
      // Bug 3: store the timer ref so it can be cancelled on unmount
      if (algoliaStatusClearTimerRef.current) clearTimeout(algoliaStatusClearTimerRef.current);
      algoliaStatusClearTimerRef.current = setTimeout(() => setSyncStatus(null), 3000);
    }
  }, [loadEmailsByIds]);

  // Store searchEmailsAlgolia in a ref to avoid useEffect dependency issues
  const searchEmailsAlgoliaRef = useRef(searchEmailsAlgolia);
  searchEmailsAlgoliaRef.current = searchEmailsAlgolia;

  // Algolia auto-debounce DISABLED. Email search is single-source via
  // Graph $search now (`runTrueGraphSearch` below — triggered by the
  // user clicking the Microsoft 365 search button). The Algolia leg
  // was previously firing on every keystroke as a "fast path" but it
  // duplicated emails across two indexes, drifted out of sync as
  // Algolia re-scored, and produced different results on repeat runs.
  // Microsoft Graph is the source of truth for email; we don't search
  // a separate index of our own copy.
  //
  // The keep-Algolia-alive comment block has been retained so the
  // state variables don't trip the lint pass — but they're no longer
  // wired to any input. The `searchEmailsAlgoliaHttp` HTTP function
  // stays deployed for the AI assistant's customer-history tool which
  // searches CHATS + LEARNED NOTES only (not emails).
  useEffect(() => {
    // Clear any leftover debounce timer from a prior version of this
    // effect. The state resets here also keep the empty-state UI clean.
    if (algoliaSearchDebounceRef.current) {
      clearTimeout(algoliaSearchDebounceRef.current);
      algoliaSearchDebounceRef.current = null;
    }
    setLastSearchResultIds([]);
    setAlgoliaSearchLatency(null);
    lastAlgoliaQueryRef.current = '';
  }, [searchTerm]);

  const runTrueGraphSearch = useCallback(async (days: number, searchEntireMailbox: boolean = false) => {
    const term = String(searchTerm || '').trim();
    if (!term) return;

    // Rate limiting: prevent rapid-fire searches
    const now = Date.now();
    const timeSinceLastSearch = now - lastGraphSearchTimeRef.current;
    if (timeSinceLastSearch < GRAPH_SEARCH_MIN_INTERVAL_MS) {
      const remaining = Math.ceil((GRAPH_SEARCH_MIN_INTERVAL_MS - timeSinceLastSearch) / 1000);
      setSearchCooldownRemaining(remaining);
      setSyncStatus(`Please wait ${remaining}s before searching again`);
      setTimeout(() => {
        setSearchCooldownRemaining(0);
        setSyncStatus(null);
      }, GRAPH_SEARCH_MIN_INTERVAL_MS - timeSinceLastSearch);
      return;
    }

    // In-flight tracking: prevent concurrent searches
    if (isGraphSearchInFlightRef.current) {
      setSyncStatus('Search already in progress...');
      return;
    }

    const mailboxList = selectedMailbox !== 'all' ? [selectedMailbox] : undefined;
    const key = `${term}::${days}::${mailboxList ? mailboxList.join(',') : 'all'}::${searchEntireMailbox}`;
    // Avoid duplicate calls (e.g. handleSearchMore + effects)
    if (lastGraphSearchKeyRef.current === key) return;
    lastGraphSearchKeyRef.current = key;

    isGraphSearchInFlightRef.current = true;
    lastGraphSearchTimeRef.current = now;
    setIsSyncingGraph(true);
    setSyncStatus(searchEntireMailbox 
      ? 'Deep searching entire mailbox (this may take up to 2 minutes)...' 
      : 'Searching Microsoft 365...');

    try {
      const resp = await fetchWithTimeout(
        'https://us-central1-web-construct-app.cloudfunctions.net/searchGraphMail',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            query: term,
            mailboxes: mailboxList,
            // For "Search Entire Mailbox": request more pages to find older emails
            maxPages: searchEntireMailbox ? 20 : 3, // Start smaller to avoid timeouts
            top: 50,
            limit: searchEntireMailbox ? 500 : 150,
            searchEntireMailbox,
          }),
        },
        searchEntireMailbox ? 180000 : 90000 // 3 min for full mailbox, 90s for quick (multi-folder search)
      );

      if (!resp.ok) {
        const errorData = await resp.json().catch(() => ({}));
        const errorMsg = errorData?.error || 'Search failed';
        // Check for rate limit errors
        if (resp.status === 429 || errorMsg.toLowerCase().includes('rate') || errorMsg.toLowerCase().includes('throttl')) {
          setSyncStatus('Too many requests - please wait a moment');
        } else if (resp.status === 500) {
          setSyncStatus('Server error - showing cached results');
          console.error('[runTrueGraphSearch] Server 500 error:', errorData);
        } else {
          setSyncStatus('Search failed - showing cached results');
        }
        return;
      }

      // Parse response to get the IDs of emails that were found/upserted
      const responseData = await resp.json().catch(() => ({}));
      const items = responseData?.items || [];
      const totalFound = responseData?.totalFound || items.length;
      
      console.log('[runTrueGraphSearch] Backend response:', {
        ok: responseData?.ok,
        totalFound,
        itemsCount: items.length,
        mailboxesSearched: responseData?.mailboxesSearched,
        mailboxes: responseData?.mailboxes,
        partialErrors: responseData?.partialErrors,
        message: responseData?.message,
        query: responseData?.query,
        version: responseData?.version, // Check this to verify deployment!
      });
      
      // If zero results, log more details
      if (items.length === 0) {
        console.warn('[runTrueGraphSearch] Zero results from Graph. Check:');
        console.warn('  - Backend version:', responseData?.version || 'NOT DEPLOYED - deploy first!');
        console.warn('  - Mailboxes searched:', responseData?.mailboxes);
        console.warn('  - Errors:', responseData?.partialErrors);
      }
      
      // Microsoft Graph's $search is server-side full-text search across the ENTIRE mailbox.
      // It searches subject, body, from, to, etc. efficiently on Microsoft's servers.
      // We just need to load the specific results it found - no need to load entire date ranges.
      
      if (items.length > 0) {
        // Extract the Firestore document IDs from the items
        // The backend now returns docId directly from upsertMessage
        const firestoreIds: string[] = items
          .filter((item: any) => item.docId)
          .map((item: any) => item.docId);
        
        console.log('[runTrueGraphSearch] Firestore IDs to load:', firestoreIds.length, 'of', items.length, 'items');
        
        if (firestoreIds.length > 0) {
          setSyncStatus(`Loading ${firestoreIds.length} search results...`);
          await loadEmailsByIds(firestoreIds);
          
          // Track these IDs so user can save the search
          setLastSearchResultIds(firestoreIds);
          
          // Check for partial errors in the response
          const partialErrors = responseData?.partialErrors;
          if (partialErrors && partialErrors.length > 0) {
            console.warn('[runTrueGraphSearch] Some mailboxes had errors:', partialErrors);
            setSyncStatus(`Found ${totalFound} emails (some mailboxes had errors)`);
          } else {
            setSyncStatus(`Found ${totalFound} emails`);
          }
        } else {
          // Backend returned items but without docIds - likely old backend version
          console.warn('[runTrueGraphSearch] Items returned but no docIds - backend needs deployment');
          console.log('[runTrueGraphSearch] Sample item:', items[0]);
          setSyncStatus('Deploy backend for search results');
        }
      } else {
        // No results from Graph search - could be:
        // 1. Search term not found in any emails
        // 2. Backend error (check partialErrors)
        // 3. No mailboxes configured
        const partialErrors = responseData?.partialErrors;
        if (partialErrors && partialErrors.length > 0) {
          console.warn('[runTrueGraphSearch] Search had errors:', partialErrors);
          setSyncStatus('Search errors - showing local results');
        } else if (responseData?.message) {
          console.log('[runTrueGraphSearch] Backend message:', responseData.message);
          setSyncStatus(responseData.message);
        } else {
          console.log('[runTrueGraphSearch] No Graph matches, showing local results only');
          setSyncStatus('No new matches in mailbox');
        }
      }
      
      // Note: Local filtering happens automatically via filteredEmails useMemo
      // which filters preLoadedEmails by searchTerm. So local matches will show instantly.
      // Clicking "Search Microsoft 365" fetches additional results from Graph API.
    } catch (e: any) {
      const msg = e?.message || String(e || '');
      if (msg.includes('timed out')) setSyncStatus('Search taking longer than expected - showing results');
      else if (msg.includes('abort')) setSyncStatus('Search cancelled');
      else setSyncStatus('Search unavailable - showing cached results');
      console.error('[runTrueGraphSearch] Error:', e);
    } finally {
      isGraphSearchInFlightRef.current = false;
      setIsSyncingGraph(false);
      setTimeout(() => setSyncStatus(null), 4000);
    }
  // Bug 6: dependencies for useCallback (fetchWithTimeout is a stable utility — no captured state)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, selectedMailbox, loadEmailsByIds]);

  // Handler for searching Microsoft 365 (Graph API)
  const handleSearchMicrosoft365 = async () => {
    if (!searchTerm?.trim()) return;
    
    // Clear to force new search
    lastGraphSearchKeyRef.current = '';
    
    // Always do full mailbox search - no point in partial searches that timeout anyway
    await runTrueGraphSearch(365 * 5, true);
  };

  // Handler for checking new emails (delta sync - like Outlook Send/Receive)
  const handleSyncMailbox = async () => {
    if (isSyncingMailbox) return;
    
    setIsSyncingMailbox(true);
    setSyncStatus('Checking for new emails...');
    
    try {
      const resp = await authFetch(
        'https://us-central1-web-construct-app.cloudfunctions.net/checkNewEmails',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({}),
        }
      );
      
      if (resp.ok) {
        const data = await resp.json().catch(() => ({}));
        const totalNew = data?.totalNew || 0;
        const totalDeleted = data?.totalDeleted || 0;
        
        // Record successful sync time
        setLastSyncedAt(new Date());
        
        if (totalNew > 0 || totalDeleted > 0) {
          const parts = [];
          if (totalNew > 0) parts.push(`${totalNew} new`);
          if (totalDeleted > 0) parts.push(`${totalDeleted} removed`);
          setSyncStatus(parts.join(', '));
          
          // Reload recent emails to show the new ones
          const now = new Date();
          const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          await loadEmailsForDateRange(weekAgo, now);
        } else {
          setSyncStatus('Up to date');
        }
        console.log('[Check New Emails] Complete:', data);
      } else {
        const errorData = await resp.json().catch(() => ({}));
        console.error('[Check New Emails] Failed:', errorData);
        setSyncStatus('Sync failed');
      }
    } catch (error: any) {
      console.error('[Check New Emails] Error:', error);
      setSyncStatus('Sync error');
    } finally {
      setIsSyncingMailbox(false);
      setTimeout(() => setSyncStatus(null), 3000);
    }
  };

  useEffect(() => {
    const currentTheme = document.documentElement.getAttribute('data-bs-theme') || 'light';
    setThemeState(currentTheme);
    const observer = new MutationObserver(() => {
        setThemeState(document.documentElement.getAttribute('data-bs-theme') || 'light');
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-bs-theme'] });
    return () => observer.disconnect();
  }, []);

  // Reset pagination when tab or search term changes
  useEffect(() => {
    setDisplayCount(30);
  }, [activeTab, searchTerm]);

  // If a selected custom folder becomes invalid (deleted or converted to a group), fall back to Inbox.
  useEffect(() => {
    if (!activeTab.startsWith('f_')) return;
    const f = customFolders.find((x) => x.id === activeTab);
    if (!f) setActiveTab('inbox');
    else if (f.isGroup === true) setActiveTab('inbox');
  }, [activeTab, customFolders]);

  // Refresh "time ago" display every minute
  const [, setTimeRefresh] = useState(0);
  useEffect(() => {
    if (!lastSyncedAt) return;
    const interval = setInterval(() => setTimeRefresh((n) => n + 1), 60000);
    return () => clearInterval(interval);
  }, [lastSyncedAt]);

  // Persist search term and date range across panel view changes
  useEffect(() => {
    try {
      const savedTerm = localStorage.getItem('emailFeed.searchTerm');
      if (savedTerm !== null) setSearchTerm(savedTerm);
    } catch {}
  }, []);

  useEffect(() => {
    try { localStorage.setItem('emailFeed.searchTerm', searchTerm); } catch {}
  }, [searchTerm]);

  // Bug 1 + Bug 3 + UX 1: cancel all pending timers on unmount to prevent state updates on an unmounted component
  useEffect(() => {
    return () => {
      // Cancel all hover-prefetch debounce timers
      Object.values(hoverPrefetchTimersRef.current).forEach((id) => window.clearTimeout(id));
      hoverPrefetchTimersRef.current = {};
      // Cancel the Algolia status-clear timer
      if (algoliaStatusClearTimerRef.current) {
        clearTimeout(algoliaStatusClearTimerRef.current);
        algoliaStatusClearTimerRef.current = null;
      }
      // Cancel the Algolia search debounce timer
      if (algoliaSearchDebounceRef.current) {
        clearTimeout(algoliaSearchDebounceRef.current);
        algoliaSearchDebounceRef.current = null;
      }
      // Cancel any pending undo toast timer (UX 1)
      setUndoToast((prev) => {
        if (prev) clearTimeout(prev.timerId);
        return null;
      });
    };
  }, []);

  // Reset search state when search term changes
  useEffect(() => {
    setSearchLookbackDays(365 * 5); // Always search full history when Graph search is triggered
    setLastSearchResultIds([]); // Clear previous search results
  }, [searchTerm]);

  // NOTE: Automatic Graph search removed - it always timed out (45s) because even "quick" 
  // searches take 60+ seconds. Now we only trigger Graph search when user clicks the button.
  // Local filtering via filteredEmails useMemo provides instant feedback as user types.

  const formatDate = (date: Date): string => {
    const day = ("0" + date.getDate()).slice(-2);
    const month = ("0" + (date.getMonth() + 1)).slice(-2);
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const startOfDay = (d: Date): Date => {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
  };

  const { defaultWindowStart, searchWindowStart } = useMemo(() => {
    const now = new Date();
    const defaultWindowStart = startOfDay(new Date(now));
    defaultWindowStart.setDate(defaultWindowStart.getDate() - 30);

    const searchWindowStart = startOfDay(new Date(now));
    searchWindowStart.setDate(searchWindowStart.getDate() - searchLookbackDays);

    return { defaultWindowStart, searchWindowStart };
  }, [searchLookbackDays]);

  // Compute available mailboxes from loaded emails
  const availableMailboxes = useMemo(() => {
    const mailboxSet = new Set<string>();
    preLoadedEmails.forEach(email => {
      if (email.mailbox) mailboxSet.add(email.mailbox);
    });
    // Sort alphabetically
    return Array.from(mailboxSet).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
  }, [preLoadedEmails]);

  const filteredEmails = useMemo(() => {
    const lowerSearchTerm = searchTerm ? searchTerm.toLowerCase() : '';

    const base = preLoadedEmails.map((email) => {
      // Re-calculate virtualFolderId live so rules apply immediately without page reload
      let vId = email.virtualFolderId;
      const senderAddr = email.from?.[0]?.address || (email as any).senderEmail;
      const physicalFolder = normalizeEmailFolder(email);

      if (senderAddr && (physicalFolder === 'inbox' || physicalFolder === 'junk')) {
        const match = customFolders.find(f => 
          f.senderEmails.some(e => e.toLowerCase() === senderAddr.toLowerCase())
        );
        vId = match ? match.id : undefined;
      }
      return { ...email, virtualFolderId: vId };
    }).filter((email) => {
      const emailDate = email.date.toDate();

      // When searching, don't restrict by date - Algolia/Graph already searched all dates
      // Only apply date filter when browsing (no search term)
      if (!searchTerm) {
        // Default feed scope: last 30 days
        if (emailDate < defaultWindowStart) return false;
      }

      // Mailbox filter: if a specific mailbox is selected, only show emails from that mailbox
      if (selectedMailbox !== 'all' && email.mailbox !== selectedMailbox) {
        return false;
      }

      if (!searchTerm) return true;
      
      // Local text search - matches emails already in preLoadedEmails
      return (
        (email.title?.toLowerCase().includes(lowerSearchTerm)) ||
        (email.sender?.toLowerCase().includes(lowerSearchTerm)) ||
        (email.message?.toLowerCase().includes(lowerSearchTerm))
      );
    });

    // Sort flagged emails to the top, then newest first (`pinned` is the legacy field name).
    return base.sort((a, b) => {
      if (a.pinned && !b.pinned) return -1;
      if (!a.pinned && b.pinned) return 1;
      return b.createdAt.getTime() - a.createdAt.getTime();
    });
  }, [preLoadedEmails, searchTerm, defaultWindowStart, customFolders, selectedMailbox]);

  const tabCounts = useMemo(() => {
    return filteredEmails.reduce((acc, email) => {
      // Folder badges should show UNREAD only (Drafts handled separately in UI).
      if (!isEmailUnread(email)) return acc;

      // 1) Physical folder
      const f = normalizeEmailFolder(email);
      // Mail filed into an Outlook folder has no tab in this feed (the terminal
      // feed has the Archive view), so it must not badge one either.
      if (isOutlookFiled(f)) return acc;
      acc[f] = (acc[f] || 0) + 1;

      // 2) Virtual folder. EmailPanelContext stamps virtualFolderId from the RAW doc, so a
      // mislabelled moved-out-of-inbox doc ({folder:'inbox', deleted:true}) can still carry a
      // sender-rule id even though it normalises to 'deleted': it must not badge a custom tab.
      if (email.virtualFolderId && f !== 'deleted') {
        acc[email.virtualFolderId] = (acc[email.virtualFolderId] || 0) + 1;
      }
      return acc;
    }, {} as Record<string, number>);
  }, [filteredEmails]);

  const visibleEmails = useMemo(() => {
    return filteredEmails.filter((e) => {
      // When searching, show ALL matching results regardless of tab/folder
      // Users expect search to span all emails, not just current folder
      if (searchTerm?.trim()) {
        return true;
      }

      const f = normalizeEmailFolder(e);
      // Outlook-filed mail has no tab here: never list it under inbox, a custom
      // folder or anywhere else (it is only reachable through search).
      if (isOutlookFiled(f)) return false;

      // If we are looking at a virtual folder
      // (Deleted mail is excluded for the same reason as in tabCounts: its stale
      // virtualFolderId must not list it as a live message in a custom folder.)
      if (activeTab.startsWith('f_')) {
        return f !== 'deleted' && e.virtualFolderId === activeTab;
      }

      if (activeTab === 'drafts') return false; // app drafts handled elsewhere
      
      // If we are looking at Inbox or Junk, exclude emails that belong to a virtual folder
      // (This matches the user request: "have emails from specific senders appear directly in these folders")
      if ((activeTab === 'inbox' || activeTab === 'junk') && e.virtualFolderId) {
        return false;
      }

      return f === activeTab;
    });
  }, [filteredEmails, activeTab, searchTerm]);

  const emailsToRender = useMemo(() => {
    return visibleEmails.slice(0, displayCount);
  }, [visibleEmails, displayCount]);

  const unreadUnpinnedVisibleCount = useMemo(() => {
    return visibleEmails.reduce((sum, e) => sum + (isEmailUnread(e) && !e.pinned ? 1 : 0), 0);
  }, [visibleEmails]);

  // NOTE: Must be a function declaration (hoisted) because it's used by earlier useMemo hooks.
  function isEmailUnread(email: FeedEmailData): boolean {
    if (email.source === 'graph') {
      // For Graph emails: prefer isRead if present; otherwise fall back to dismissed.
      const v = (email as any).isRead
      if (typeof v === 'boolean') return v === false
      return !(email as any).dismissed
    }
    return !email.dismissed;
  }

  const setGraphReadState = async (emailId: string, desiredIsRead: boolean) => {
    // Uses owner-guarded Cloud Function; requires Firebase auth token (authFetch).
    // Throw on a non-2xx so callers' optimistic updates actually roll back
    // instead of a server error silently looking like success.
    const r = await authFetch('https://us-central1-web-construct-app.cloudfunctions.net/setGraphEmailReadState', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ emailId, isRead: desiredIsRead }),
    });
    if (!r.ok) throw new Error(`read-state update failed (HTTP ${r.status})`);
  };

  const setGraphFlagState = async (emailId: string, desiredFlagged: boolean) => {
    const r = await authFetch('https://us-central1-web-construct-app.cloudfunctions.net/setGraphEmailFlagState', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ emailId, flagged: desiredFlagged }),
    });
    if (!r.ok) throw new Error(`flag-state update failed (HTTP ${r.status})`);
  };

  const handleEmailClick = (email: FeedEmailData) => {
    // Open immediately for responsiveness; any Graph work happens in the background.
    openEmailPanel('viewEmail', email as PanelEmailData);
    // On open: mark as read if unread and not pinned (background)
    if (isEmailUnread(email) && !email.pinned) {
      void handleToggleReadState(email);
    }
  };

  const scheduleGraphBodyPrefetch = (email: FeedEmailData) => {
    if (email.source !== 'graph') return
    const id = String(email.emailId || email.id || '')
    if (!id) return
    // If we don't have enough Graph metadata, don't prefetch (avoids noisy 500s).
    if (!email.mailbox || !email.graph?.id) return
    // Only prefetch when we don't already have a full body stored
    const hasFullBody = !!((email.html && email.html.length > 200) || (email.text && email.text.length > 300))
    if (hasFullBody) return
    if (hoverPrefetchTimersRef.current[id]) return

    // Debounce hover/focus so we don't spam Graph as the user moves the mouse
    hoverPrefetchTimersRef.current[id] = window.setTimeout(() => {
      delete hoverPrefetchTimersRef.current[id]
      void warmGraphEmailBodyCache(id, 'text')
    }, 200)
  }

  const isEmailRead = (email: FeedEmailData): boolean => {
    if (email.source === 'graph') {
      const v = (email as any).isRead
      if (typeof v === 'boolean') return v === true
      return (email as any).dismissed === true
    }
    return !!email.dismissed
  }

  const handleToggleJunk = async (email: FeedEmailData) => {
    const emailId = String(email.emailId || email.id || '');
    if (!emailId) return;

    // Guard against rapid double-toggling (same as pin/read/delete)
    if (pendingByEmailId[emailId]?.junk) return;
    setPendingByEmailId((prev) => ({ ...prev, [emailId]: { ...(prev[emailId] || {}), junk: true } }));

    const currentFolder = normalizeEmailFolder(email);
    const isNowJunk = currentFolder !== 'junk';
    
    try {
      // Optimistic UI update
      await updateDoc(doc(db, 'emails', emailId), {
        folder: isNowJunk ? 'junk' : 'inbox',
        quarantined: isNowJunk,
        updatedAt: serverTimestamp(),
      });

      if (email.source === 'graph') {
        const response = await authFetch('https://us-central1-web-construct-app.cloudfunctions.net/toggleGraphJunk', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ emailId, moveToJunk: isNowJunk }),
        });
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || `HTTP ${response.status}`);
        }
      }
    } catch (err) {
      console.error('Failed to toggle junk state in Graph:', err);
      // Rollback on failure
      await updateDoc(doc(db, 'emails', emailId), {
        folder: currentFolder,
        quarantined: !isNowJunk,
        updatedAt: serverTimestamp(),
      });
    } finally {
      setPendingByEmailId((prev) => ({ ...prev, [emailId]: { ...(prev[emailId] || {}), junk: false } }));
    }
  };

  const handleTogglePinned = async (email: FeedEmailData) => {
    const key = String(email.emailId || email.id || '');
    if (!key) return;
    try {
      const desired = !email.pinned;
      setPendingByEmailId((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), pin: true } }));

      // Optimistic UI: update Firestore immediately so the feed re-renders and feels instant.
      const optimisticWrite = email.source === 'graph' ? getGraphFlagWrite(desired) : { pinned: desired };
      await updateDoc(doc(db, 'emails', key), optimisticWrite);

      // Source of truth for Graph is Graph; keep Firestore in sync but don't block UI on network.
      if (email.source === 'graph') {
        try {
          await setGraphFlagState(key, desired);
        } catch (e) {
          // Revert optimistic write if Graph update fails
          await updateDoc(doc(db, 'emails', key), getGraphFlagWrite(!desired));
          throw e;
        }
      }
    } catch (error) {
      console.error("Error updating pin state:", error);
    } finally {
      setPendingByEmailId((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), pin: false } }));
    }
  }

  // Read/Unread toggle only (no pinning)
  const handleToggleReadState = async (email: FeedEmailData) => {
    const key = String(email.emailId || email.id || '');
    if (!key) return;

    // Bug 2: capture previous read state before the optimistic update so we can revert on failure
    const prevIsRead = isEmailRead(email);

    try {
      const isGraph = email.source === 'graph'
      const desiredIsRead = !prevIsRead;
      setPendingByEmailId((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), read: true } }));

      // Mirror read state into dismissed for consistent UI/badges; do not touch pinned here.
      await updateDoc(doc(db, "emails", key), { dismissed: desiredIsRead, isRead: desiredIsRead });

      // Source of truth for Graph is Graph; don't block UI.
      if (isGraph) {
        try {
          await setGraphReadState(key, desiredIsRead);
        } catch (e) {
          // Revert optimistic write if Graph update fails
          await updateDoc(doc(db, "emails", key), { dismissed: prevIsRead, isRead: prevIsRead });
          throw e;
        }
      }
    } catch (error) {
      console.error("Error updating email state:", error);
      // Bug 2: revert the optimistic Firestore update and show user feedback
      try {
        await updateDoc(doc(db, "emails", key), { dismissed: prevIsRead, isRead: prevIsRead });
      } catch (revertErr) {
        console.error("Error reverting email read state:", revertErr);
      }
      setSyncStatus("Failed to update read state — please try again");
      setTimeout(() => setSyncStatus(null), 4000);
    } finally {
      setPendingByEmailId((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), read: false } }));
    }
  };

  const handleDeleteConfirm = () => {
    if (!emailToDelete) return;

    const key = String(emailToDelete.emailId || emailToDelete.id || '');
    // Bug 4: guard against empty Firestore document key
    if (!key) {
      console.error('[EmailFeed] handleDeleteConfirm: empty email key — aborting delete');
      setShowDeleteConfirm(false);
      setEmailToDelete(null);
      return;
    }
    setShowDeleteConfirm(false);

    const emailSnapshot = emailToDelete; // capture for undo closure
    setEmailToDelete(null);

    // UX 1: show undo toast before actually deleting
    let cancelled = false;

    const performDelete = async () => {
      if (cancelled) return;
      try {
        setPendingByEmailId((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), deleting: true } }));
        if (emailSnapshot.source === 'graph') {
          await authFetch('https://us-central1-web-construct-app.cloudfunctions.net/deleteGraphEmail', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ emailId: key }),
          });
        }
        await updateDoc(doc(db, 'emails', key), { deleted: true, folder: 'deleted' });
      } catch (error) {
        console.error('Error deleting email:', error);
        setPendingByEmailId((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), deleting: false } }));
      }
    };

    const timerId = setTimeout(() => {
      setUndoToast(null);
      void performDelete();
    }, 5000);

    setUndoToast({
      message: 'Email deleted',
      onUndo: () => {
        cancelled = true;
        clearTimeout(timerId);
        setUndoToast(null);
      },
      timerId,
    });
  };

  const handleDismissAll = () => {
    const targets = visibleEmails.filter((email) => isEmailUnread(email) && !email.pinned);
    if (targets.length === 0) {
      setShowDismissAllConfirm(false);
      return;
    }

    // Close the modal immediately
    setShowDismissAllConfirm(false);

    // UX 1: capture the target list so we can restore on undo
    let cancelled = false;

    const performDismissAll = async () => {
      if (cancelled) return;
      try {
        const BATCH = 8;
        for (let i = 0; i < targets.length; i += BATCH) {
          if (cancelled) break;
          const slice = targets.slice(i, i + BATCH);
          await Promise.all(
            slice.map(async (email) => {
              const key = String(email.emailId || email.id || '');
              if (!key) return;
              if (email.source === 'graph') {
                await setGraphReadState(key, true);
                await updateDoc(doc(db, 'emails', key), { dismissed: true, isRead: true });
                return;
              }
              await updateDoc(doc(db, 'emails', key), { dismissed: true });
            })
          );
        }
      } catch (error) {
        console.error('Error dismissing all emails:', error);
      }
    };

    const timerId = setTimeout(() => {
      setUndoToast(null);
      void performDismissAll();
    }, 5000);

    setUndoToast({
      message: `${targets.length} email${targets.length !== 1 ? 's' : ''} marked as read`,
      onUndo: () => {
        cancelled = true;
        clearTimeout(timerId);
        setUndoToast(null);
      },
      timerId,
    });
  };

  const isDarkTheme = theme === 'dark' || theme === 'night';

  // UX 2: highlight matched search terms in feed rows
  const highlightMatch = useCallback((text: string, query: string): React.ReactNode => {
    if (!query || !text) return text;
    const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`(${escapedQuery})`, 'gi');
    const parts = text.split(regex);
    if (parts.length <= 1) return text;
    return (
      <>
        {parts.map((part, i) =>
          regex.test(part) ? (
            <mark
              key={i}
              style={{
                background: 'rgba(var(--bs-warning-rgb), 0.35)',
                color: 'inherit',
                padding: '0 1px',
                borderRadius: '2px',
              }}
            >
              {part}
            </mark>
          ) : (
            part
          )
        )}
      </>
    );
  }, []);

  return (
    <div
      className="email-feed"
      data-email-theme={theme}
      style={{ height: '100%', display: 'flex', flexDirection: 'row' }}
    >
      {/* Layout helpers for sidebar rows: keep ⋮ menu fixed on the right; fade long labels under it. */}
      <style>
        {`
          .email-folder-row {
            position: relative;
          }
          .email-folder-badge {
            position: absolute;
            top: 50%;
            transform: translate(-50%, -50%);
            pointer-events: none;
            z-index: 3;
          }
          .email-folder-row .email-folder-menu {
            position: absolute;
            right: 0;
            top: 50%;
            transform: translateY(-50%);
            z-index: 2;
          }
          .email-folder-row .email-folder-main {
            width: 100%;
            min-width: 0;
          }
          /* Fade-out mask for long labels (instead of pushing the ⋮ menu). */
          .email-folder-fade {
            display: block;
            min-width: 0;
            max-width: 100%;
            overflow: hidden;
            white-space: nowrap;
            text-overflow: clip;
            /* Keep most of the label fully visible; only fade right near the menu column. */
            -webkit-mask-image: linear-gradient(to right, rgba(255,255,255,1) 0%, rgba(255,255,255,1) 92%, rgba(255,255,255,0) 100%);
            mask-image: linear-gradient(to right, rgba(255,255,255,1) 0%, rgba(255,255,255,1) 92%, rgba(255,255,255,0) 100%);
          }
          /* UX 8: Mobile 44×44px minimum touch targets */
          @media (max-width: 767.98px) {
            .email-feed .btn-icon.btn-sm {
              min-width: 44px !important;
              min-height: 44px !important;
              display: inline-flex !important;
              align-items: center !important;
              justify-content: center !important;
            }
            /* Read-state toggle and kebab menu in email rows */
            .email-feed .email-feed-item .btn-icon {
              min-width: 44px !important;
              min-height: 44px !important;
            }
          }
        `}
      </style>
      {/* Metronic overrides: ONLY in dark/night, keep folder sidebar buttons transparent in all states. */}
      {isDarkTheme && (
        <style>
          {`
            /* Target ALL buttons in the folder sidebar - this is the fix for dark mode .btn.btn-light fills */
            .email-folder-sidebar .btn.btn-light,
            .email-folder-sidebar .btn.btn-light:hover,
            .email-folder-sidebar .btn.btn-light:focus,
            .email-folder-sidebar .btn.btn-light:active,
            .email-folder-sidebar .btn.btn-light.active,
            .email-folder-sidebar .btn.btn-light.show,
            .email-folder-sidebar .btn.btn-light-primary,
            .email-folder-sidebar .btn.btn-light-primary:hover,
            .email-folder-sidebar .btn.btn-light-primary:focus,
            .email-folder-sidebar .btn.btn-light-primary:active,
            .email-folder-sidebar .btn.btn-light-primary.active,
            .email-folder-sidebar .btn.btn-light-info,
            .email-folder-sidebar .btn.btn-light-info:hover,
            .email-folder-sidebar .btn.btn-light-info:focus,
            .email-folder-sidebar .btn.btn-light-info:active,
            .email-folder-sidebar .btn.btn-light-info.active,
            .email-folder-sidebar .btn.btn-active-light-primary,
            .email-folder-sidebar .btn.btn-active-light-primary:hover,
            .email-folder-sidebar .btn.btn-active-light-primary:focus,
            .email-folder-sidebar .btn.btn-active-light-primary:active,
            .email-folder-sidebar .btn.btn-active-light-info,
            .email-folder-sidebar .btn.btn-active-light-info:hover,
            .email-folder-sidebar .btn.btn-active-light-info:focus,
            .email-folder-sidebar .btn.btn-active-light-info:active {
              background: #2b2b4000 !important;
              background-color: #2b2b4000 !important;
            }
          `}
        </style>
      )}
      {/* Desktop Vertical Sidebar (Folders) */}
      <div 
        className="email-folder-sidebar d-none d-md-flex flex-column py-4 border-end" 
        style={{ 
          width: '220px', 
          backgroundColor: theme === 'dark' || theme === 'night' ? '#151521' : 'var(--kt-gray-100)',
          flexShrink: 0,
          overflowY: 'auto',
          overflowX: 'hidden'
        }}
      >
        {/* Mailbox Selector Dropdown */}
        {availableMailboxes.length > 0 && (
          <div className="px-3 mb-3">
            <label className="form-label text-muted mb-1" style={{ fontSize: '0.75rem', fontWeight: 500 }}>
              Mailbox
            </label>
            <select
              className="form-select form-select-sm"
              value={selectedMailbox}
              onChange={(e) => setSelectedMailbox(e.target.value)}
              style={{ fontSize: '0.85rem' }}
            >
              <option value="all">All Mailboxes</option>
              {availableMailboxes.map((mb) => (
                <option key={mb} value={mb}>
                  {mb}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="flex-grow-1 px-3">
          {[
            { id: 'inbox', label: 'Inbox', count: tabCounts.inbox },
            { id: 'sent', label: 'Sent', count: tabCounts.sent },
            { id: 'drafts', label: 'Drafts', count: userDrafts?.length },
            { id: 'junk', label: 'Junk', count: tabCounts.junk },
            { id: 'deleted', label: 'Trash', count: tabCounts.deleted },
          ].map((f) => (
            <React.Fragment key={f.id}>
              <button
                type="button"
                className={clsx(
                  "btn btn-sm email-folder-btn position-relative mb-2 text-start px-3 w-100 d-flex align-items-center justify-content-between",
                  activeTab === f.id ? "btn-light-primary fw-bold" : "btn-active-light-primary text-muted"
                )}
                onClick={() => {
                  setActiveTab(f.id as any);
                  if (f.id === 'drafts') setEmailPanelView('drafts');
                }}
                title={f.label}
              >
                <span style={{ fontSize: '0.9rem' }}>{f.label}</span>
                {(f.count || 0) > 0 && (
                  <span 
                    className="badge badge-circle badge-primary shadow-sm"
                    style={{ width: '20px', height: '20px', fontSize: '10px' }}
                  >
                    {f.count}
                  </span>
                )}
              </button>
              
              {/* Render custom folders under Inbox (1-level nesting: groups + leaf folders) */}
              {f.id === 'inbox' && (customFolders.length > 0) && (
                <div className="ms-3 mb-3 border-start ps-3">
                  {/* Ungrouped leaf folders */}
                  {ungroupedLeafFolders.map((cf) => (
                    <div key={cf.id} className="d-flex align-items-center mb-1 email-folder-row">
                      <button
                        type="button"
                        className={clsx(
                          "btn btn-sm email-folder-btn email-folder-main position-relative text-start px-3 w-100 d-flex align-items-center flex-grow-1",
                          activeTab === cf.id ? "btn-light-info fw-bold" : "btn-active-light-info text-muted"
                        )}
                        onClick={() => setActiveTab(cf.id)}
                        title={cf.name}
                      >
                        <span className="flex-grow-1" style={{ minWidth: 0 }}>
                          <span className="email-folder-fade" style={{ fontSize: '0.85rem' }}>{cf.name}</span>
                        </span>
                        {(tabCounts[cf.id] || 0) > 0 && (
                          <span 
                            className="badge badge-circle badge-info shadow-sm"
                            style={{ width: '18px', height: '18px', fontSize: '9px', position: 'absolute', right: 44, top: '50%', transform: 'translateY(-50%)' }}
                          >
                            {tabCounts[cf.id]}
                          </span>
                        )}
                      </button>

                      <div className="dropdown email-folder-menu">
                        <button
                          type="button"
                          className="btn btn-icon btn-sm btn-light btn-active-light-primary"
                          data-bs-toggle="dropdown"
                          aria-expanded="false"
                          onClick={(e) => e.stopPropagation()}
                          style={{ width: 34, height: 34 }}
                          title="Folder options"
                        >
                          <i className="fas fa-ellipsis-v fs-8"></i>
                        </button>
                        <ul className="dropdown-menu dropdown-menu-end shadow-sm py-2">
                          <li>
                            <button
                              className="dropdown-item d-flex align-items-center py-2"
                              onClick={(e) => {
                                e.stopPropagation();
                                openEditFolderModal({ id: cf.id, name: cf.name });
                              }}
                            >
                              <i className="fas fa-pen fs-8 me-2 text-muted"></i>
                              Rename / Move
                            </button>
                          </li>
                          <li>
                            <button
                              className="dropdown-item d-flex align-items-center py-2 text-danger"
                              onClick={(e) => {
                                e.stopPropagation();
                                openDeleteFolderModal({ id: cf.id, name: cf.name });
                              }}
                            >
                              <i className="fas fa-trash fs-8 me-2 text-danger"></i>
                              Delete
                            </button>
                          </li>
                        </ul>
                      </div>
                    </div>
                  ))}

                  {/* Groups + children */}
                  {groupFolders
                    .slice()
                    .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                    .map((g) => {
                      const expanded = expandedGroupIds[g.id] ?? true;
                      const children = leafByGroupId[g.id] || [];
                      if (children.length === 0) {
                        // Still render empty groups so users can rename/delete them.
                      }

                      const treeLineColor = (theme === 'dark' || theme === 'night') ? '#323248' : 'var(--bs-border-color)'
                      const childLabelPaddingLeft = 40 // px; indents only the label/button, not the menu icon column

                      return (
                        <div key={g.id} className="mb-2">
                          <div className="d-flex align-items-center email-folder-row">
                            <button
                              type="button"
                              className={clsx(
                                "btn btn-sm email-folder-btn email-folder-main position-relative text-start px-3 w-100 d-flex align-items-center flex-grow-1",
                                "btn-active-light-info text-muted"
                              )}
                              onClick={() => toggleGroupExpanded(g.id)}
                              title={g.name}
                            >
                              <span className="d-flex align-items-center flex-grow-1" style={{ minWidth: 0 }}>
                                <i className={clsx("fas me-2", expanded ? "fa-chevron-down" : "fa-chevron-right")} style={{ fontSize: 10 }}></i>
                                <i className="fas fa-folder-open fs-9 me-2 text-muted"></i>
                                <span className="flex-grow-1" style={{ minWidth: 0 }}>
                                  <span className="email-folder-fade" style={{ fontSize: '0.85rem' }}>{g.name}</span>
                                </span>
                              </span>
                            </button>

                            <div className="dropdown email-folder-menu">
                              <button
                                type="button"
                                className="btn btn-icon btn-sm btn-light btn-active-light-primary"
                                data-bs-toggle="dropdown"
                                aria-expanded="false"
                                onClick={(e) => e.stopPropagation()}
                                style={{ width: 34, height: 34 }}
                                title="Group options"
                              >
                                <i className="fas fa-ellipsis-v fs-8"></i>
                              </button>
                              <ul className="dropdown-menu dropdown-menu-end shadow-sm py-2">
                                <li>
                                  <button
                                    className="dropdown-item d-flex align-items-center py-2"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openEditFolderModal({ id: g.id, name: g.name });
                                    }}
                                  >
                                    <i className="fas fa-pen fs-8 me-2 text-muted"></i>
                                    Rename
                                  </button>
                                </li>
                                <li>
                                  <button
                                    className={clsx(
                                      "dropdown-item d-flex align-items-center py-2 text-danger",
                                      children.length > 0 && "disabled"
                                    )}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (children.length > 0) return;
                                      openDeleteFolderModal({ id: g.id, name: g.name });
                                    }}
                                    title={children.length > 0 ? 'Delete subfolders first' : 'Delete group'}
                                  >
                                    <i className="fas fa-trash fs-8 me-2 text-danger"></i>
                                    Delete
                                  </button>
                                </li>
                              </ul>
                            </div>
                          </div>

                          {expanded && children.length > 0 && (
                            <div className="mt-2 position-relative">
                              {/* Vertical guide line for nested folders (does not affect menu column alignment) */}
                              <div
                                aria-hidden="true"
                                style={{
                                  position: 'absolute',
                                  left: 18,
                                  top: 2,
                                  bottom: 2,
                                  width: 1,
                                  backgroundColor: treeLineColor,
                                  opacity: 0.7,
                                }}
                              />

                              {children.map((cf) => (
                                <div key={cf.id} className="d-flex align-items-center mb-1 email-folder-row">
                                  {(tabCounts[cf.id] || 0) > 0 && (
                                    <span
                                      className="badge badge-circle badge-info shadow-sm email-folder-badge"
                                      style={{ left: 18, width: '18px', height: '18px', fontSize: '9px' }}
                                    >
                                      {tabCounts[cf.id]}
                                    </span>
                                  )}
                                  <button
                                    type="button"
                                    className={clsx(
                                      "btn btn-sm email-folder-btn email-folder-main position-relative text-start pe-3 w-100 d-flex align-items-center flex-grow-1",
                                      activeTab === cf.id ? "btn-light-info fw-bold" : "btn-active-light-info text-muted"
                                    )}
                                    style={{ paddingLeft: childLabelPaddingLeft }}
                                    onClick={() => setActiveTab(cf.id)}
                                    title={cf.name}
                                  >
                                    <span className="flex-grow-1" style={{ minWidth: 0 }}>
                                      <span className="email-folder-fade" style={{ fontSize: '0.85rem' }}>{cf.name}</span>
                                    </span>
                                  </button>

                                  <div className="dropdown email-folder-menu">
                                    <button
                                      type="button"
                                      className="btn btn-icon btn-sm btn-light btn-active-light-primary"
                                      data-bs-toggle="dropdown"
                                      aria-expanded="false"
                                      onClick={(e) => e.stopPropagation()}
                                      style={{ width: 34, height: 34 }}
                                      title="Folder options"
                                    >
                                      <i className="fas fa-ellipsis-v fs-8"></i>
                                    </button>
                                    <ul className="dropdown-menu dropdown-menu-end shadow-sm py-2">
                                      <li>
                                        <button
                                          className="dropdown-item d-flex align-items-center py-2"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            openEditFolderModal({ id: cf.id, name: cf.name });
                                          }}
                                        >
                                          <i className="fas fa-pen fs-8 me-2 text-muted"></i>
                                          Rename / Move
                                        </button>
                                      </li>
                                      <li>
                                        <button
                                          className="dropdown-item d-flex align-items-center py-2 text-danger"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            openDeleteFolderModal({ id: cf.id, name: cf.name });
                                          }}
                                        >
                                          <i className="fas fa-trash fs-8 me-2 text-danger"></i>
                                          Delete
                                        </button>
                                      </li>
                                    </ul>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                </div>
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Create Folder button at bottom of sidebar */}
        <div className="px-4 mt-auto pt-4 border-top">
          <button 
            type="button" 
            className="btn btn-sm btn-outline btn-outline-dashed btn-outline-default bg-transparent w-100 py-2 d-flex align-items-center justify-content-center"
            onClick={() => { setFolderCreateError(null); setShowCreateFolderModal(true); }}
            style={{ backgroundColor: 'transparent' }}
          >
            <i className="fas fa-plus fs-7 me-2"></i>
            <span style={{ fontSize: '0.85rem' }}>New Folder</span>
          </button>
        </div>
      </div>

      <div className="flex-grow-1 d-flex flex-column" style={{ minWidth: 0 }}>
        {/* Sticky Header (Search/Filter + Global Actions) */}
        <div
          className='p-3 border-bottom'
          style={{
            position: "sticky",
            top: 0,
            zIndex: 1, // Ensure it's above the scrollable content but below panel header
            backgroundColor: theme === 'dark' || theme === 'night' ? '#1e1e2d' : 'var(--kt-gray-100)',
            boxShadow: theme === 'dark' || theme === 'night' ? '0 5px 15px rgba(0,0,0,0.3)' : '0 5px 15px rgba(0,0,0,0.1)', // Adjusted shadow for more prominence
          }}
        >
          {/* Row 1: Search (defaults to last 30 days until user taps "Search more") */}
          <div className="px-2">
            <div className="d-flex align-items-center mb-2" style={{ gap: '0.5rem' }}>
              <div className="flex-grow-1" style={{ minWidth: 0 }}>
                <input
                  type="text"
                  className="form-control form-control-sm w-100"
                  placeholder="Search emails (instant)..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {/* Algolia instant search indicator */}
                {isSearchingAlgolia && (
                  <span className="spinner-border spinner-border-sm text-primary" role="status" aria-hidden="true" style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)' }}></span>
                )}
              </div>

              {isLoadingRange && (
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
              )}

              {/* Saved Searches Dropdown */}
              {savedSearches.length > 0 && (
                <div className="dropdown">
                  <button
                    type="button"
                    className="btn btn-sm btn-light-primary dropdown-toggle"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                    title="Saved Searches"
                    disabled={isLoadingSavedSearch}
                  >
                    {isLoadingSavedSearch ? (
                      <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    ) : (
                      <i className="fas fa-bookmark fs-7"></i>
                    )}
                    <span className="d-none d-sm-inline ms-1">{savedSearches.length}</span>
                  </button>
                  <ul className="dropdown-menu dropdown-menu-end shadow" style={{ minWidth: '250px', maxHeight: '300px', overflowY: 'auto' }}>
                    <li className="dropdown-header fw-bold">Saved Searches</li>
                    <li><hr className="dropdown-divider my-1" /></li>
                    {savedSearches.map((ss) => (
                      <li key={ss.id}>
                        {renamingSearchId === ss.id ? (
                          /* UX 4: inline rename input */
                          <div
                            className="dropdown-item d-flex align-items-center py-2"
                            style={{ gap: '0.4rem' }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={renameSearchValue}
                              autoFocus
                              onChange={(e) => setRenameSearchValue(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleCommitRenameSearch(ss.id);
                                if (e.key === 'Escape') { setRenamingSearchId(null); setRenameSearchValue(''); }
                              }}
                              onBlur={() => handleCommitRenameSearch(ss.id)}
                              style={{ minWidth: 0, flex: 1 }}
                            />
                          </div>
                        ) : (
                          <button
                            className="dropdown-item d-flex align-items-center justify-content-between py-2"
                            onClick={() => handleLoadSavedSearch(ss)}
                          >
                            <div className="d-flex flex-column" style={{ minWidth: 0, flex: 1 }}>
                              <span className="fw-semibold text-truncate" style={{ maxWidth: '160px' }}>
                                {ss.name || ss.query}
                              </span>
                              <small className="text-muted">
                                {ss.resultCount} emails • {ss.createdAt.toLocaleDateString()}
                              </small>
                            </div>
                            {/* UX 4: pencil icon to start inline rename */}
                            <button
                              className="btn btn-sm btn-icon btn-light ms-1"
                              onClick={(e) => {
                                e.stopPropagation();
                                setRenamingSearchId(ss.id);
                                setRenameSearchValue(ss.name || ss.query);
                              }}
                              title="Rename saved search"
                            >
                              <i className="fas fa-pen fs-9"></i>
                            </button>
                            <button
                              className="btn btn-sm btn-icon btn-light-danger ms-1"
                              onClick={(e) => handleDeleteSavedSearch(ss.id, e)}
                              title="Delete saved search"
                            >
                              <i className="fas fa-times fs-8"></i>
                            </button>
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Sync Mailbox Button - quick check for new emails */}
              <button
                type="button"
                className="btn btn-sm btn-light-info"
                onClick={handleSyncMailbox}
                title={lastSyncedAt ? `Check for new emails (last synced ${formatTimeAgo(lastSyncedAt)})` : 'Check for new emails from Microsoft 365'}
                disabled={isSyncingMailbox || isSyncingGraph}
              >
                {isSyncingMailbox ? (
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                ) : (
                  <i className="fas fa-sync fs-7"></i>
                )}
              </button>
              {/* Last synced indicator */}
              {lastSyncedAt && !isSyncingMailbox && !syncStatus && (
                <span className="text-muted fs-9 d-none d-sm-inline" style={{ whiteSpace: 'nowrap' }}>
                  {formatTimeAgo(lastSyncedAt)}
                </span>
              )}

              {/* Search Microsoft 365 Button - for deep search of unsynced emails */}
              {searchTerm?.trim() && (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={handleSearchMicrosoft365}
                  title={searchCooldownRemaining > 0 ? `Please wait ${searchCooldownRemaining}s` : 'Deep search Microsoft 365 for emails not yet synced (slower)'}
                  disabled={isSyncingGraph || searchCooldownRemaining > 0 || isSearchingAlgolia}
                >
                  {isSyncingGraph ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                      <span className="d-none d-sm-inline">Searching…</span>
                    </>
                  ) : searchCooldownRemaining > 0 ? (
                    /* UX 3: visible cooldown feedback */
                    <>
                      <i className="fas fa-clock fs-7"></i>
                      <span className="d-none d-sm-inline ms-1">{searchCooldownRemaining}s</span>
                    </>
                  ) : (
                    <>
                      <i className="fas fa-cloud-download-alt fs-7"></i>
                      <span className="d-none d-sm-inline ms-1">Deep Search</span>
                    </>
                  )}
                </button>
              )}

              {/* Save Search Button - appears when we have search results to save */}
              {lastSearchResultIds.length > 0 && searchTerm?.trim() && (
                <button
                  type="button"
                  className="btn btn-sm btn-light-success"
                  onClick={() => {
                    setSaveSearchName(searchTerm.trim());
                    setShowSaveSearchModal(true);
                  }}
                  title={`Save this search (${lastSearchResultIds.length} emails)`}
                  disabled={isSavingSearch}
                >
                  {isSavingSearch ? (
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                  ) : (
                    <>
                      <i className="fas fa-save fs-7"></i>
                      <span className="d-none d-sm-inline ms-1">Save</span>
                    </>
                  )}
                </button>
              )}
            </div>
          </div>

          {/* Row 2: Mailbox selector (mobile) + Filter dropdown + Compose + Mark All as Read (inline, equal width) */}
          <div className="px-2">
            <div className="d-flex align-items-center" style={{ gap: '0.5rem' }}>
              {/* Mobile: Mailbox Selector */}
              {availableMailboxes.length > 0 && (
                <div className="dropdown d-md-none" style={{ minWidth: 0, flexShrink: 0 }}>
                  <button
                    type="button"
                    className="btn btn-sm btn-light dropdown-toggle text-truncate"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                    title="Select Mailbox"
                    style={{ maxWidth: '120px' }}
                  >
                    <i className="fas fa-at fs-7 me-1"></i>
                    {selectedMailbox === 'all' ? 'All' : selectedMailbox.split('@')[0]}
                  </button>
                  <ul className="dropdown-menu shadow-sm" style={{ maxHeight: '50vh', overflowY: 'auto' }}>
                    <li>
                      <button 
                        className={clsx('dropdown-item', { active: selectedMailbox === 'all' })} 
                        onClick={() => setSelectedMailbox('all')}
                      >
                        All Mailboxes
                      </button>
                    </li>
                    <li><hr className="dropdown-divider" /></li>
                    {availableMailboxes.map((mb) => (
                      <li key={mb}>
                        <button 
                          className={clsx('dropdown-item text-truncate', { active: selectedMailbox === mb })} 
                          onClick={() => setSelectedMailbox(mb)}
                          style={{ maxWidth: '250px' }}
                        >
                          {mb}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Mobile: Folders dropdown */}
              <div className="dropdown d-md-none flex-grow-1" style={{ minWidth: 0 }}>
                <button
                  type="button"
                  className="btn btn-sm btn-light dropdown-toggle w-100 text-truncate"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                  title="Folders"
                >
                  <KTSVG path='/media/icons/duotune/general/gen059.svg' className='svg-icon-4 me-1' />
                  {activeTab === 'inbox' && `Inbox (${tabCounts.inbox || 0})`}
                  {activeTab === 'sent' && `Sent (${tabCounts.sent || 0})`}
                  {activeTab === 'drafts' && `Drafts (${userDrafts?.length || 0})`}
                  {activeTab === 'junk' && `Junk (${tabCounts.junk || 0})`}
                  {activeTab === 'deleted' && `Deleted (${tabCounts.deleted || 0})`}
                  {activeTab.startsWith('f_') && `${customFolders.find(cf => cf.id === activeTab)?.name || 'Folder'} (${tabCounts[activeTab] || 0})`}
                </button>
                <ul className="dropdown-menu shadow-sm" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                  <li><button className={clsx('dropdown-item', { active: activeTab === 'inbox' })} onClick={() => setActiveTab('inbox')}>Inbox ({tabCounts.inbox || 0})</button></li>
                  
                  {/* Custom Folders in Mobile Dropdown */}
                  {(customFolders.length > 0) && (
                    <>
                      {/* Ungrouped leaf folders */}
                      {ungroupedLeafFolders.map((cf) => (
                        <li key={cf.id}>
                          <div
                            className={clsx(
                              'dropdown-item ps-8 d-flex align-items-center justify-content-between',
                              { active: activeTab === cf.id }
                            )}
                            style={{ gap: '0.5rem' }}
                          >
                            <div
                              role="button"
                              tabIndex={0}
                              className="d-flex align-items-center flex-grow-1 text-truncate"
                              onClick={() => setActiveTab(cf.id)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') setActiveTab(cf.id);
                              }}
                              title={cf.name}
                              style={{ cursor: 'pointer', minWidth: 0 }}
                            >
                              <i className="fas fa-folder fs-9 me-2 text-info"></i>
                              <span className="text-truncate">
                                {cf.name} ({tabCounts[cf.id] || 0})
                              </span>
                            </div>

                            <div className="d-flex align-items-center flex-shrink-0" style={{ gap: '0.25rem' }}>
                              <button
                                type="button"
                                className="btn btn-sm btn-icon btn-light"
                                title="Rename / Move"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openEditFolderModal({ id: cf.id, name: cf.name });
                                }}
                              >
                                <i className="fas fa-pen fs-9"></i>
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-icon btn-light-danger"
                                title="Delete"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openDeleteFolderModal({ id: cf.id, name: cf.name });
                                }}
                              >
                                <i className="fas fa-trash fs-9"></i>
                              </button>
                            </div>
                          </div>
                        </li>
                      ))}

                      {/* Groups + children (always expanded in mobile dropdown) */}
                      {groupFolders
                        .slice()
                        .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                        .map((g) => {
                          const children = leafByGroupId[g.id] || [];
                          return (
                            <React.Fragment key={g.id}>
                              <li className="dropdown-header ps-8 py-2 d-flex align-items-center justify-content-between">
                                <span className="text-truncate" title={g.name}>
                                  <i className="fas fa-folder-open fs-9 me-2 text-muted"></i>
                                  {g.name}
                                </span>
                                <span className="d-flex align-items-center" style={{ gap: '0.25rem' }}>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-icon btn-light"
                                    title="Rename group"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openEditFolderModal({ id: g.id, name: g.name });
                                    }}
                                  >
                                    <i className="fas fa-pen fs-9"></i>
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-icon btn-light-danger"
                                    title={children.length > 0 ? 'Delete subfolders first' : 'Delete group'}
                                    disabled={children.length > 0}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (children.length > 0) return;
                                      openDeleteFolderModal({ id: g.id, name: g.name });
                                    }}
                                  >
                                    <i className="fas fa-trash fs-9"></i>
                                  </button>
                                </span>
                              </li>
                              {children.map((cf) => (
                                <li key={cf.id}>
                                  <div
                                    className={clsx(
                                      'dropdown-item ps-10 d-flex align-items-center justify-content-between',
                                      { active: activeTab === cf.id }
                                    )}
                                    style={{ gap: '0.5rem' }}
                                  >
                                    <div
                                      role="button"
                                      tabIndex={0}
                                      className="d-flex align-items-center flex-grow-1 text-truncate"
                                      onClick={() => setActiveTab(cf.id)}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') setActiveTab(cf.id);
                                      }}
                                      title={cf.name}
                                      style={{ cursor: 'pointer', minWidth: 0 }}
                                    >
                                      <i className="fas fa-folder fs-9 me-2 text-info"></i>
                                      <span className="text-truncate">
                                        {cf.name} ({tabCounts[cf.id] || 0})
                                      </span>
                                    </div>
                                    <div className="d-flex align-items-center flex-shrink-0" style={{ gap: '0.25rem' }}>
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-icon btn-light"
                                        title="Rename / Move"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openEditFolderModal({ id: cf.id, name: cf.name });
                                        }}
                                      >
                                        <i className="fas fa-pen fs-9"></i>
                                      </button>
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-icon btn-light-danger"
                                        title="Delete"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openDeleteFolderModal({ id: cf.id, name: cf.name });
                                        }}
                                      >
                                        <i className="fas fa-trash fs-9"></i>
                                      </button>
                                    </div>
                                  </div>
                                </li>
                              ))}
                            </React.Fragment>
                          );
                        })}
                    </>
                  )}

                  <li><button className={clsx('dropdown-item', { active: activeTab === 'sent' })} onClick={() => setActiveTab('sent')}>Sent ({tabCounts.sent || 0})</button></li>
                  <li><button className={clsx('dropdown-item', { active: activeTab === 'junk' })} onClick={() => setActiveTab('junk')}>Junk ({tabCounts.junk || 0})</button></li>
                  <li><button className={clsx('dropdown-item', { active: activeTab === 'deleted' })} onClick={() => setActiveTab('deleted')}>Deleted ({tabCounts.deleted || 0})</button></li>
                  <li><hr className="dropdown-divider" /></li>
                  <li>
                    <button
                      className={clsx('dropdown-item', { active: activeTab === 'drafts' })}
                      onClick={() => {
                        setActiveTab('drafts');
                        setEmailPanelView('drafts');
                      }}
                    >
                      Drafts ({userDrafts?.length || 0})
                    </button>
                  </li>
                  <li><hr className="dropdown-divider" /></li>
                  <li>
                    <button
                      className="dropdown-item py-2"
                      onClick={() => { setFolderCreateError(null); setShowCreateFolderModal(true); }}
                    >
                      <i className="fas fa-plus fs-9 me-2"></i>
                      New Folder
                    </button>
                  </li>
                </ul>
              </div>

              {/* Action Buttons (Compose, Mark Read) */}
              <button
                type="button"
                className="btn btn-sm btn-primary flex-grow-1 w-100"
                onClick={() => setEmailPanelView('composeEmail')}
              >
                <KTSVG path='/media/icons/duotune/arrows/arr075.svg' className='svg-icon-3 me-1' />
                Compose
              </button>

              {/* UX 6: Mark all as read — confirm only when there are more than 10 unread */}
              <button
                type="button"
                className="btn btn-sm btn-light flex-grow-1 w-100 text-truncate"
                onClick={() => {
                  if (unreadUnpinnedVisibleCount > 10) {
                    setShowDismissAllConfirm(true);
                  } else {
                    // Fewer than 10 unread — skip confirmation, go straight to undo toast
                    handleDismissAll();
                  }
                }}
                disabled={unreadUnpinnedVisibleCount === 0}
                title={unreadUnpinnedVisibleCount > 0 ? `Mark all ${unreadUnpinnedVisibleCount} unread emails as read` : 'No unread emails'}
              >
                <span className="d-none d-sm-inline">Mark All as Read</span>
                <span className="d-inline d-sm-none">Mark Read</span>
                {unreadUnpinnedVisibleCount > 0 && (
                  <span className="badge badge-circle badge-primary ms-2" style={{ width: '18px', height: '18px', fontSize: '9px' }}>
                    {unreadUnpinnedVisibleCount}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>

      {/* UX 7: Sync progress indicator — always visible during any sync operation */}
      {(isSyncingGraph || isSyncingMailbox || isSearchingAlgolia) && (
        <div
          style={{
            padding: '0.4rem 1rem',
            backgroundColor: isDarkTheme ? '#1a1a2e' : 'var(--bs-primary-bg-subtle)',
            borderBottom: `1px solid ${isDarkTheme ? '#2e2e48' : 'var(--bs-primary-border-subtle)'}`,
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            fontSize: '0.82rem',
            color: isDarkTheme ? '#8b8fd8' : 'var(--bs-primary-text-emphasis)',
          }}
          role="status"
          aria-live="polite"
        >
          <span
            className="spinner-border spinner-border-sm"
            role="status"
            aria-hidden="true"
            style={{ width: '12px', height: '12px', flexShrink: 0 }}
          ></span>
          <span>
            {isSyncingGraph
              ? (syncStatus || 'Searching Microsoft 365…')
              : isSyncingMailbox
              ? (syncStatus || 'Syncing emails…')
              : 'Searching…'}
          </span>
        </div>
      )}

      {/* Scrollable Email List */}
      <div style={{ flexGrow: 1, overflowY: 'auto', padding: '0.75rem 1rem' }}>
        {isLoadingInitialEmails ? (
          <div className="p-3 text-center">
            <span className={clsx({ "text-white-75": theme === 'dark' || theme === 'night', "text-muted": theme !== 'dark' && theme !== 'night' })}>Loading emails...</span>
          </div>
        ) : filteredEmails.length === 0 ? (
          <div className="p-3 text-center">
            <span className={clsx({ "text-white-75": theme === 'dark' || theme === 'night', "text-muted": theme !== 'dark' && theme !== 'night' })}>
              {searchTerm ? 'No local emails match your search.' : 'No emails found.'}
            </span>
            {/* Deep Search M365 button when no instant results */}
            {searchTerm && (
              <div className="mt-4">
                <div className="d-flex flex-column gap-2 align-items-center">
                  {isSearchingAlgolia ? (
                    <div className="text-muted">
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Searching...
                    </div>
                  ) : (
                    <>
                      <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        onClick={handleSearchMicrosoft365}
                        disabled={isSyncingGraph || searchCooldownRemaining > 0}
                        title="Deep search Microsoft 365 for emails not yet synced"
                      >
                        {isSyncingGraph ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Deep searching M365...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-cloud-download-alt fs-7 me-2"></i>
                            Deep Search M365
                          </>
                        )}
                      </button>
                    </>
                  )}
                  
                  <div className="text-muted fs-8 mt-1">
                    Search your entire mailbox history (may take up to 2 minutes)
                  </div>
                </div>
                
                {/* Sync status indicator */}
                {(isSyncingGraph || syncStatus) && (
                  <div className={`fs-8 mt-2 d-flex align-items-center justify-content-center ${
                    syncStatus?.includes('complete') || syncStatus?.includes('Found') 
                      ? 'text-success' 
                      : syncStatus?.includes('wait') || syncStatus?.includes('rate') || syncStatus?.includes('throttl')
                        ? 'text-warning'
                        : syncStatus?.includes('error') || syncStatus?.includes('failed')
                          ? 'text-danger'
                          : 'text-info'
                  }`}>
                    {isSyncingGraph && (
                      <span className="spinner-border spinner-border-sm me-2" style={{ width: '12px', height: '12px' }} role="status" aria-hidden="true"></span>
                    )}
                    {syncStatus?.includes('complete') && <i className="fas fa-check-circle me-2"></i>}
                    {(syncStatus?.includes('error') || syncStatus?.includes('failed')) && <i className="fas fa-exclamation-circle me-2"></i>}
                    {(syncStatus?.includes('wait') || syncStatus?.includes('rate')) && <i className="fas fa-clock me-2"></i>}
                    {syncStatus}
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <>
            {emailsToRender.map((email) => {
              const isUnread = !isEmailRead(email);
              const cardStyle: React.CSSProperties = {
                cursor: "pointer",
                border: "1px solid",
                borderColor: 'var(--kt-card-border-color)',
                backgroundColor: 'var(--kt-card-bg)',
                borderRadius: "0.475rem", // Metronic card border radius
                boxShadow: isUnread ? "0 0.125rem 0.25rem rgba(0, 0, 0, 0.075)" : "none", // Subtle shadow for unread
                minHeight: "120px", // Adjusted height
                display: "flex",
                flexDirection: "column",
                marginBottom: "1rem",
                position: 'relative'
              };
              return (
                <div
                  key={email.emailId}
                  className={clsx("card email-feed-item", { "unread-email": isUnread })}
                  style={cardStyle}
                  onClick={() => handleEmailClick(email)}
                  onMouseEnter={() => scheduleGraphBodyPrefetch(email)}
                  onFocus={() => scheduleGraphBodyPrefetch(email)}
                  tabIndex={0}
                >
                  {/* ... item content ... */}
                  <div className="card-header d-flex justify-content-between align-items-center p-2" style={{ flexWrap: "nowrap", minHeight: 'auto' }}>
                      <div className="d-flex align-items-center flex-grow-1 me-2" style={{ minWidth: 0 }}>
                          <div className="flex-grow-1" style={{ minWidth: 0 }}>
                              <div style={{ display: "flex", alignItems: "center", whiteSpace: "nowrap", marginBottom: '0.25rem' }}>
                                  <small className="text-muted me-2">{formatDate(email.createdAt)}</small>
                                  <span className={`badge badge-light-${email.emailType === 'sent' ? 'success' : 'primary'} me-2`}>{email.emailType === 'sent' ? 'Sent' : 'Inbox'}</span>
                                  {email.pinned && <span className="badge badge-danger me-2">Flagged</span>}
                              </div>
                              <h6
                                  className={clsx(
                                    "mb-0 text-truncate",
                                    isUnread ? "fw-bolder" : "fw-normal",
                                    theme === 'dark' || theme === 'night' ? 'text-white' : 'text-dark'
                                  )}
                                  title={email.title}
                                  style={{ fontSize: isUnread ? '1rem' : '0.95rem' }}
                              >
                                  {/* UX 2: highlight search term in subject */}
                                  {searchTerm?.trim() ? highlightMatch(email.title, searchTerm.trim()) : email.title}
                              </h6>
                              {email.sender && (
                                <small className={clsx(
                                  "d-block text-truncate",
                                  isUnread ? "text-primary fw-bold" : "text-muted fw-semibold"
                                )}>
                                  {/* UX 2: highlight search term in sender */}
                                  From: {searchTerm?.trim() ? highlightMatch(email.sender, searchTerm.trim()) : email.sender}
                                </small>
                              )}
                          </div>
                      </div>
                      <div className="d-flex flex-shrink-0 align-items-center dropdown">
                        <button
                          type="button"
                          className="btn btn-icon btn-sm btn-light btn-active-light-primary me-1 position-relative"
                          onClick={(e) => { e.stopPropagation(); handleToggleReadState(email); }}
                          title={isUnread ? "Mark as Read" : "Mark as Unread"}
                          style={{ width: 38, height: 38, minWidth: 38, minHeight: 38 }}
                          disabled={!!pendingByEmailId[String(email.emailId || email.id || '')]?.read}
                          aria-busy={!!pendingByEmailId[String(email.emailId || email.id || '')]?.read}
                        >
                          <span style={{ opacity: pendingByEmailId[String(email.emailId || email.id || '')]?.read ? 0 : 1 }}>
                            <KTSVG
                              path={isUnread ? '/media/icons/duotune/communication/com002.svg' : '/media/icons/duotune/communication/com010.svg'}
                              className={clsx('svg-icon-4', isUnread && 'text-primary')}
                            />
                          </span>
                          {pendingByEmailId[String(email.emailId || email.id || '')]?.read && (
                            <span className="position-absolute top-50 start-50 translate-middle spinner-border spinner-border-sm text-primary" role="status" aria-hidden="true"></span>
                          )}
                        </button>
                        <button
                          type="button"
                          className="btn btn-icon btn-sm btn-light btn-active-light-primary position-relative"
                          data-bs-toggle="dropdown"
                          aria-expanded="false"
                          onClick={(e) => e.stopPropagation()}
                          style={{ width: 38, height: 38, minWidth: 38, minHeight: 38 }}
                        >
                          {pendingByEmailId[String(email.emailId || email.id || '')]?.deleting ? (
                            <span className="spinner-border spinner-border-sm text-danger" role="status" aria-hidden="true"></span>
                          ) : (
                            <i className="fas fa-ellipsis-v fs-6"></i>
                          )}
                        </button>
                        <ul className="dropdown-menu dropdown-menu-end shadow-sm py-2" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                          <li>
                            <button 
                              className="dropdown-item d-flex align-items-center py-2" 
                              onClick={(e) => { e.stopPropagation(); handleTogglePinned(email); }}
                            >
                              <KTSVG
                                path={email.pinned ? '/media/icons/duotune/general/gen015.svg' : '/media/icons/duotune/general/gen056.svg'}
                                className={`svg-icon-4 me-2 ${email.pinned ? 'text-danger' : ''}`}
                              />
                              {email.pinned ? 'Remove flag' : 'Flag'}
                              {pendingByEmailId[String(email.emailId || email.id || '')]?.pin && (
                                <span className="ms-auto spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                              )}
                            </button>
                          </li>
                          <li>
                            <button 
                              className="dropdown-item d-flex align-items-center py-2" 
                              onClick={(e) => { e.stopPropagation(); handleToggleReadState(email); }}
                            >
                              <KTSVG
                                path={!isUnread ? '/media/icons/duotune/communication/com002.svg' : '/media/icons/duotune/communication/com010.svg'}
                                className={`svg-icon-4 me-2 ${isUnread ? 'text-muted' : 'text-primary'}`}
                              />
                              {isUnread ? 'Mark as Read' : 'Mark as Unread'}
                              {pendingByEmailId[String(email.emailId || email.id || '')]?.read && (
                                <span className="ms-auto spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                              )}
                            </button>
                          </li>
                          <li>
                            <button 
                              className="dropdown-item d-flex align-items-center py-2" 
                              onClick={(e) => { e.stopPropagation(); handleToggleJunk(email); }}
                              disabled={!!pendingByEmailId[String(email.emailId || email.id || '')]?.junk}
                            >
                              <i className={clsx("fas fs-6 me-2", activeTab === 'junk' ? "fa-inbox text-success" : "fa-ban text-warning")}></i>
                              {activeTab === 'junk' ? 'Not Junk (Move to Inbox)' : 'Mark as Junk'}
                              {pendingByEmailId[String(email.emailId || email.id || '')]?.junk && (
                                <span className="ms-auto spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                              )}
                            </button>
                          </li>
                          <li><hr className="dropdown-divider" /></li>
                          
                          {/* Move to Folder Section */}
                          <li className="dropdown-header py-1 text-uppercase fs-9 fw-bold">Move to Folder</li>
                          {leafFolders.length === 0 && (
                            <li><span className="dropdown-item-text text-muted fs-8 py-1">No custom folders</span></li>
                          )}
                          {leafFolders.map(cf => (
                            <li key={cf.id}>
                              <button 
                                className={clsx(
                                  "dropdown-item d-flex align-items-center py-1 fs-8",
                                  email.virtualFolderId === cf.id && "bg-light-info text-info"
                                )}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const sender = email.from?.[0]?.address || (email as any).senderEmail;
                                  if (sender) addSenderToFolder(cf.id, sender);
                                }}
                              >
                                <i className={clsx("fas fa-folder me-2", email.virtualFolderId === cf.id ? "text-info" : "text-muted")}></i>
                                {cf.name}
                              </button>
                            </li>
                          ))}
                          
                          <li><hr className="dropdown-divider" /></li>
                          
                          {/* Link to Job Section */}
                          {(() => {
                            const trueCustomerId = getTrueCustomerId(email);
                            const customerJobs = trueCustomerId ? getJobsForCustomer(trueCustomerId) : [];
                            const customerName = trueCustomerId ? getCustomerName(trueCustomerId) : null;
                            const isLinking = linkingEmailId === (email.emailId || email.id);
                            
                            return (
                              <>
                                <li className="dropdown-header py-1 text-uppercase fs-9 fw-bold d-flex align-items-center">
                                  <i className="fas fa-link me-1"></i>
                                  Link to Job
                                  {customerName && (
                                    <span className="badge badge-light-primary ms-2 fs-9 fw-normal">{customerName}</span>
                                  )}
                                </li>
                                {email.linkedJobId && (
                                  <li>
                                    <span className="dropdown-item-text text-success fs-8 py-1 d-flex align-items-center">
                                      <i className="fas fa-check-circle me-1"></i>
                                      Linked to: {email.linkedJobLabel || email.linkedJobId}
                                    </span>
                                  </li>
                                )}
                                {/* Auto-suggested jobs from detected customer */}
                                {customerJobs.map((job) => (
                                  <li key={job.id}>
                                    <button 
                                      className={clsx(
                                        "dropdown-item d-flex align-items-center py-1 fs-8",
                                        email.linkedJobId === job.id && "bg-light-success text-success"
                                      )}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleLinkToJob(email.emailId || email.id, job.id, job.jobLabel);
                                      }}
                                      disabled={isLinking}
                                    >
                                      <i className={clsx(
                                        "fas me-2",
                                        email.linkedJobId === job.id ? "fa-check text-success" : "fa-briefcase text-muted"
                                      )}></i>
                                      <span className="flex-grow-1 text-truncate" style={{ maxWidth: '150px' }}>
                                        {job.jobLabel}
                                      </span>
                                      <span className={`badge badge-light-${job.status === 'Active' ? 'danger' : job.status === 'Open' ? 'primary' : 'warning'} fs-9 ms-1`}>
                                        {job.status}
                                      </span>
                                      {isLinking && (
                                        <span className="ms-1 spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                      )}
                                    </button>
                                  </li>
                                ))}
                                {/* Manual search option */}
                                <li>
                                  <button 
                                    className="dropdown-item d-flex align-items-center py-2 fs-8 text-primary"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openLinkModal(email);
                                    }}
                                  >
                                    <i className="fas fa-search me-2"></i>
                                    Search other customer...
                                  </button>
                                </li>
                                {/* Create new job option */}
                                <li>
                                  <button 
                                    className="dropdown-item d-flex align-items-center py-2 fs-8 text-success"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openCreateJobModal(email, trueCustomerId);
                                    }}
                                  >
                                    <i className="fas fa-plus-circle me-2"></i>
                                    Create New Job...
                                  </button>
                                </li>
                                <li><hr className="dropdown-divider" /></li>
                              </>
                            );
                          })()}
                          
                          <li>
                            <button 
                              className="dropdown-item d-flex align-items-center py-2 text-danger" 
                              onClick={(e) => { e.stopPropagation(); setEmailToDelete(email); setShowDeleteConfirm(true); }}
                            >
                              <KTSVG path="/media/icons/duotune/general/gen027.svg" className="svg-icon-4 me-2 text-danger" />
                              Delete
                            </button>
                          </li>
                        </ul>
                      </div>
                  </div>
                  <div className="card-body p-2" style={{ flexGrow: 1, overflow: "hidden" }}>
                      <p
                          className={`mb-0 fs-7 ${theme === 'dark' || theme === 'night' ? 'text-gray-400' : 'text-muted'}`}
                          style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden"}}
                      >
                          {email.message}
                      </p>
                  </div>
                </div>
              );
            })}
            
            {/* UX 5: Load more indicator — shows count context and load more */}
            {visibleEmails.length > displayCount ? (
              <div className="text-center mt-3 mb-3">
                <div className={clsx("fs-8 mb-2", isDarkTheme ? "text-gray-500" : "text-muted")}>
                  Showing {Math.min(displayCount, visibleEmails.length)} of {visibleEmails.length} emails
                </div>
                <button
                  type="button"
                  className="btn btn-sm btn-light-primary"
                  onClick={() => setDisplayCount(prev => prev + 50)}
                >
                  Load more
                </button>
              </div>
            ) : (
              <>
                {/* Load older emails from database when we've shown all locally-loaded emails */}
                {hasMoreOlderEmails && !searchTerm && (
                  <div className="text-center mt-3 mb-5">
                    <div className={clsx("fs-8 mb-2", isDarkTheme ? "text-gray-500" : "text-muted")}>
                      Showing all {visibleEmails.length} email{visibleEmails.length !== 1 ? 's' : ''} in view
                    </div>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline btn-outline-dashed btn-outline-primary"
                      onClick={loadOlderEmails}
                      disabled={isLoadingOlderEmails}
                    >
                      {isLoadingOlderEmails ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Loading older emails...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-history fs-7 me-2"></i>
                          Load older emails
                        </>
                      )}
                    </button>
                  </div>
                )}

                {/* All emails shown */}
                {!hasMoreOlderEmails && visibleEmails.length > 0 && !searchTerm && (
                  <div className="text-center mt-3 mb-5">
                    <span className={clsx("fs-7", isDarkTheme ? "text-gray-500" : "text-muted")}>
                      <i className="fas fa-check-circle me-2 text-success"></i>
                      All {visibleEmails.length} email{visibleEmails.length !== 1 ? 's' : ''} shown
                    </span>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {/* Deep search button at bottom of results */}
        {searchTerm && filteredEmails.length > 0 && (
          <div className="p-3 text-center border-top">
            <div className="d-flex flex-column gap-2 align-items-center">
              <div className="text-muted fs-8 mb-2">
                {algoliaSearchLatency !== null ? (
                  <>Found {filteredEmails.length} results in {algoliaSearchLatency}ms</>
                ) : (
                  <>Showing {filteredEmails.length} results</>
                )}
              </div>
              
              <button
                type="button"
                className="btn btn-sm btn-light-secondary"
                onClick={handleSearchMicrosoft365}
                disabled={isSyncingGraph || searchCooldownRemaining > 0 || isSearchingAlgolia}
                title="Deep search Microsoft 365 for emails not yet synced (slower)"
              >
                {isSyncingGraph ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Deep searching M365...
                  </>
                ) : (
                  <>
                    <i className="fas fa-cloud-download-alt fs-7 me-2"></i>
                    Deep search M365 for more
                  </>
                )}
              </button>
            </div>
            
            {/* Sync status indicator */}
            {(isSyncingGraph || syncStatus) && (
              <div className={`fs-8 mt-2 d-flex align-items-center justify-content-center ${
                syncStatus?.includes('complete') || syncStatus?.includes('Found') 
                  ? 'text-success' 
                  : syncStatus?.includes('wait') || syncStatus?.includes('rate') || syncStatus?.includes('throttl')
                    ? 'text-warning'
                    : syncStatus?.includes('error') || syncStatus?.includes('failed')
                      ? 'text-danger'
                      : 'text-info'
              }`}>
                {isSyncingGraph && (
                  <span className="spinner-border spinner-border-sm me-2" style={{ width: '12px', height: '12px' }} role="status" aria-hidden="true"></span>
                )}
                {syncStatus?.includes('complete') && <i className="fas fa-check-circle me-2"></i>}
                {(syncStatus?.includes('error') || syncStatus?.includes('failed')) && <i className="fas fa-exclamation-circle me-2"></i>}
                {(syncStatus?.includes('wait') || syncStatus?.includes('rate')) && <i className="fas fa-clock me-2"></i>}
                {syncStatus}
              </div>
            )}
          </div>
        )}
      </div>

      {/* UX 1: Undo toast for destructive actions */}
      {undoToast && (
        <div
          style={{
            position: 'fixed',
            bottom: '1.5rem',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            backgroundColor: 'var(--kt-tooltip-bg)',
            color: 'var(--kt-tooltip-color)',
            borderRadius: '0.5rem',
            padding: '0.6rem 1rem',
            boxShadow: '0 4px 20px rgba(0,0,0,0.35)',
            fontSize: '0.9rem',
            whiteSpace: 'nowrap',
          }}
          role="status"
          aria-live="polite"
        >
          <span>{undoToast.message}</span>
          <button
            type="button"
            onClick={undoToast.onUndo}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--bs-warning)',
              fontWeight: 600,
              cursor: 'pointer',
              padding: '0',
              fontSize: '0.9rem',
              textDecoration: 'underline',
            }}
          >
            Undo
          </button>
          <button
            type="button"
            onClick={dismissUndoToast}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--kt-tooltip-color)',
              opacity: 0.6,
              cursor: 'pointer',
              padding: '0',
              fontSize: '0.8rem',
              lineHeight: 1,
            }}
            aria-label="Dismiss"
          >
            ✕
          </button>
        </div>
      )}

      {/* Confirmation Modals (local to this component) */}
      {showDeleteConfirm && emailToDelete && (
        <div className="modal fade show" style={{ display: "block", zIndex: 1055 }} aria-modal="true" role="dialog">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{backgroundColor: 'var(--bs-body-bg)'}}>
              <div className="modal-header p-3">
                <h5 className={`modal-title ${theme === 'dark' || theme === 'night' ? 'text-white' : 'text-body'}`}>Confirm Deletion</h5>
                <button type="button" className={`btn-close btn-sm ${theme === 'dark' || theme === 'night' ? 'btn-close-white' : ''}`} onClick={() => setShowDeleteConfirm(false)}></button>
              </div>
              <div className={`modal-body p-3 ${theme === 'dark' || theme === 'night' ? 'text-white-75' : 'text-muted'}`}>Are you sure you want to delete the email "{emailToDelete.subject}"? This action cannot be undone.</div>
              <div className="modal-footer p-2">
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowDeleteConfirm(false)}>Cancel</button>
                <button type="button" className="btn btn-danger btn-sm" onClick={handleDeleteConfirm}>Delete</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showDismissAllConfirm && (
         <div className="modal fade show" style={{ display: "block", zIndex: 1055 }} aria-modal="true" role="dialog">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{backgroundColor: 'var(--bs-body-bg)'}}>
              <div className="modal-header p-3">
                <h5 className={`modal-title ${theme === 'dark' || theme === 'night' ? 'text-white' : 'text-body'}`}>Mark All as Read</h5>
                <button type="button" className={`btn-close btn-sm ${theme === 'dark' || theme === 'night' ? 'btn-close-white' : ''}`} onClick={() => setShowDismissAllConfirm(false)}></button>
              </div>
              <div className={`modal-body p-3 ${theme === 'dark' || theme === 'night' ? 'text-white-75' : 'text-muted'}`}>Are you sure you want to mark all visible, unread emails as read? This will not affect flagged emails.</div>
              <div className="modal-footer p-2">
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowDismissAllConfirm(false)}>Cancel</button>
                <button type="button" className="btn btn-primary btn-sm" onClick={handleDismissAll}>Mark as Read</button>
              </div>
            </div>
          </div>
        </div>
      )}
       {(showDeleteConfirm || showDismissAllConfirm) && <div className="modal-backdrop fade show" style={{zIndex: 1051}}></div>}

      {/* Create Folder Modal */}
      {showCreateFolderModal && (
        <div className="modal fade show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1100 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg">
              <div className="modal-header py-4">
                <h5 className="modal-title fw-bold">Create New Folder</h5>
                <button type="button" className="btn-close" onClick={() => setShowCreateFolderModal(false)}></button>
              </div>
              <div className="modal-body py-6">
                <div className="mb-5">
                  <label className="form-label fw-semibold mb-2">Folder Name</label>
                  <input
                    type="text"
                    className="form-control form-control-solid"
                    placeholder="e.g. Invoices, Clients, etc."
                    value={newFolderName}
                    onChange={(e) => setNewFolderName(e.target.value)}
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && newFolderName.trim()) {
                        const parentId = newFolderIsGroup ? null : (newFolderParentId || null);
                        setFolderCreateError(null);
                        createCustomFolder(newFolderName, { isGroup: newFolderIsGroup, parentId })
                          .then((id) => {
                            if (newFolderIsGroup) {
                              setExpandedGroupIds((prev) => ({ ...prev, [id]: true }));
                            } else {
                              if (parentId) setExpandedGroupIds((prev) => ({ ...prev, [parentId]: true }));
                              setActiveTab(id);
                            }
                            setNewFolderName('');
                            setNewFolderIsGroup(false);
                            setNewFolderParentId('');
                            setShowCreateFolderModal(false);
                          })
                          // Bug 5: surface error to user
                          .catch((err) => {
                            console.error('[EmailFeed] create folder failed', err);
                            setFolderCreateError('Failed to create folder — please try again.');
                          });
                      }
                    }}
                  />
                  <div className="form-text mt-2 text-muted fs-8">
                    Create a folder (sender rules) or a group (container). Only folders can receive senders.
                  </div>
                </div>

                <div className="mb-4">
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      id="newFolderIsGroup"
                      checked={newFolderIsGroup}
                      onChange={(e) => {
                        setNewFolderIsGroup(e.target.checked);
                        if (e.target.checked) setNewFolderParentId('');
                      }}
                    />
                    <label className="form-check-label fw-semibold" htmlFor="newFolderIsGroup">
                      Create as group (header)
                    </label>
                  </div>
                </div>

                {!newFolderIsGroup && groupFolders.length > 0 && (
                  <div className="mb-2">
                    <label className="form-label fw-semibold mb-2">Group (optional)</label>
                    <select
                      className="form-select form-select-solid"
                      value={newFolderParentId}
                      onChange={(e) => setNewFolderParentId(e.target.value)}
                    >
                      <option value="">No group</option>
                      {groupFolders
                        .slice()
                        .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                        .map((g) => (
                          <option key={g.id} value={g.id}>
                            {g.name}
                          </option>
                        ))}
                    </select>
                  </div>
                )}
              </div>
              {/* Bug 5: folder creation error feedback */}
              {folderCreateError && (
                <div className="px-6 pb-2">
                  <div className="alert alert-danger py-2 px-3 mb-0 fs-8 d-flex align-items-center">
                    <i className="fas fa-exclamation-circle me-2 text-danger"></i>
                    {folderCreateError}
                  </div>
                </div>
              )}
              <div className="modal-footer border-0 py-4">
                <button
                  type="button"
                  className="btn btn-sm btn-light"
                  onClick={() => {
                    setShowCreateFolderModal(false);
                    setNewFolderName('');
                    setNewFolderIsGroup(false);
                    setNewFolderParentId('');
                    setFolderCreateError(null);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-primary px-6"
                  disabled={!newFolderName.trim()}
                  onClick={async () => {
                    const parentId = newFolderIsGroup ? null : (newFolderParentId || null);
                    setFolderCreateError(null);
                    try {
                      const id = await createCustomFolder(newFolderName, { isGroup: newFolderIsGroup, parentId });
                      if (newFolderIsGroup) {
                        setExpandedGroupIds((prev) => ({ ...prev, [id]: true }));
                      } else {
                        if (parentId) setExpandedGroupIds((prev) => ({ ...prev, [parentId]: true }));
                        setActiveTab(id);
                      }
                      setNewFolderName('');
                      setNewFolderIsGroup(false);
                      setNewFolderParentId('');
                      setShowCreateFolderModal(false);
                    } catch (err) {
                      // Bug 5: surface error to user instead of swallowing it
                      console.error('[EmailFeed] create folder failed', err);
                      setFolderCreateError('Failed to create folder — please try again.');
                    }
                  }}
                >
                  {newFolderIsGroup ? 'Create Group' : 'Create Folder'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Save Search Modal */}
      {showSaveSearchModal && (
        <div className="modal fade show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1100 }}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '400px' }}>
            <div className="modal-content border-0 shadow-lg">
              <div className="modal-header py-3 border-bottom">
                <h5 className="modal-title fw-bold fs-5">
                  <i className="fas fa-bookmark text-success me-2"></i>
                  Save Search Results
                </h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => {
                    setShowSaveSearchModal(false);
                    setSaveSearchName('');
                  }}
                ></button>
              </div>
              
              <div className="modal-body py-4">
                <div className="mb-4">
                  <label className="form-label fw-semibold">Search Name</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="e.g., Revived Aesthetics Emails"
                    value={saveSearchName}
                    onChange={(e) => setSaveSearchName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && saveSearchName.trim()) {
                        handleSaveSearch();
                      }
                    }}
                    autoFocus
                  />
                  <div className="text-muted fs-8 mt-2">
                    This will save {lastSearchResultIds.length} email{lastSearchResultIds.length !== 1 ? 's' : ''} for quick access later.
                  </div>
                </div>
                
                <div className="bg-light-primary rounded p-3">
                  <div className="d-flex align-items-center">
                    <i className="fas fa-info-circle text-primary me-2"></i>
                    <span className="fs-8">
                      Saved searches load instantly — no need to wait for Microsoft 365 to search again.
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="modal-footer border-0 py-3">
                <button 
                  type="button" 
                  className="btn btn-sm btn-light" 
                  onClick={() => {
                    setShowSaveSearchModal(false);
                    setSaveSearchName('');
                  }}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="btn btn-sm btn-success px-6"
                  disabled={!saveSearchName.trim() || isSavingSearch}
                  onClick={handleSaveSearch}
                >
                  {isSavingSearch ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save me-2"></i>
                      Save Search
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Folder Modal */}
      {showEditFolderModal && folderToEdit && (
        <div className="modal fade show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1100 }}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '420px' }}>
            <div className="modal-content border-0 shadow-lg">
              <div className="modal-header py-3 border-bottom">
                <h5 className="modal-title fw-bold fs-5">
                  <i className="fas fa-pen text-primary me-2"></i>
                  {folderToEdit.isGroup ? 'Rename Group' : 'Edit Folder'}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowEditFolderModal(false);
                    setFolderToEdit(null);
                    setEditFolderName('');
                    setEditFolderParentId('');
                  }}
                ></button>
              </div>

              <div className="modal-body py-4">
                <label className="form-label fw-semibold">Folder Name</label>
                <input
                  type="text"
                  className="form-control"
                  value={editFolderName}
                  onChange={(e) => setEditFolderName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && editFolderName.trim()) {
                      const desiredParentId = folderToEdit.isGroup ? null : (editFolderParentId || null);
                      Promise.resolve()
                        .then(async () => {
                          await renameCustomFolder(folderToEdit.id, editFolderName);
                          if (!folderToEdit.isGroup && (folderToEdit.parentId || null) !== desiredParentId) {
                            await setCustomFolderParent(folderToEdit.id, desiredParentId);
                            if (desiredParentId) setExpandedGroupIds((prev) => ({ ...prev, [desiredParentId]: true }));
                          }
                        })
                        .then(() => {
                          setShowEditFolderModal(false);
                          setFolderToEdit(null);
                          setEditFolderName('');
                          setEditFolderParentId('');
                        })
                        .catch((err) => console.error('[EmailFeed] edit folder failed', err));
                    }
                  }}
                  autoFocus
                />

                {!folderToEdit.isGroup && groupFolders.length > 0 && (
                  <div className="mt-4">
                    <label className="form-label fw-semibold">Group (optional)</label>
                    <select
                      className="form-select"
                      value={editFolderParentId}
                      onChange={(e) => setEditFolderParentId(e.target.value)}
                    >
                      <option value="">No group</option>
                      {groupFolders
                        .filter((g) => g.id !== folderToEdit.id)
                        .slice()
                        .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                        .map((g) => (
                          <option key={g.id} value={g.id}>
                            {g.name}
                          </option>
                        ))}
                    </select>
                    <div className="text-muted fs-8 mt-2">
                      Moving a folder between groups does not change its sender rules.
                    </div>
                  </div>
                )}
              </div>

              <div className="modal-footer border-0 py-3">
                <button
                  type="button"
                  className="btn btn-sm btn-light"
                  onClick={() => {
                    setShowEditFolderModal(false);
                    setFolderToEdit(null);
                    setEditFolderName('');
                    setEditFolderParentId('');
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-primary px-6"
                  disabled={!editFolderName.trim()}
                  onClick={async () => {
                    const desiredParentId = folderToEdit.isGroup ? null : (editFolderParentId || null);
                    try {
                      await renameCustomFolder(folderToEdit.id, editFolderName);
                      if (!folderToEdit.isGroup && (folderToEdit.parentId || null) !== desiredParentId) {
                        await setCustomFolderParent(folderToEdit.id, desiredParentId);
                        if (desiredParentId) setExpandedGroupIds((prev) => ({ ...prev, [desiredParentId]: true }));
                      }
                    } catch (err) {
                      console.error('[EmailFeed] edit folder failed', err);
                    } finally {
                      setShowEditFolderModal(false);
                      setFolderToEdit(null);
                      setEditFolderName('');
                      setEditFolderParentId('');
                    }
                  }}
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Folder Confirmation */}
      {showDeleteFolderConfirm && folderToDelete && (
        <div className="modal fade show" style={{ display: "block", zIndex: 1100 }} aria-modal="true" role="dialog">
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '460px' }}>
            <div className="modal-content">
              <div className="modal-header py-3 border-bottom">
                <h5 className="modal-title fw-bold">
                  <i className="fas fa-trash text-danger me-2"></i>
                  Delete Folder
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowDeleteFolderConfirm(false);
                    setFolderToDelete(null);
                  }}
                ></button>
              </div>
              <div className="modal-body py-4">
                <div className="fw-semibold mb-2">
                  Are you sure you want to delete "{folderToDelete.name}"?
                </div>
                <div className="text-muted fs-8">
                  {folderToDelete.isGroup
                    ? 'This removes the group header. (Groups must be empty to delete.)'
                    : 'This removes the folder and its sender rules. Emails will fall back to Inbox/Junk based on their normal folder.'}
                </div>
              </div>
              <div className="modal-footer border-0 py-3">
                <button
                  type="button"
                  className="btn btn-sm btn-light"
                  onClick={() => {
                    setShowDeleteFolderConfirm(false);
                    setFolderToDelete(null);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-danger px-6"
                  onClick={async () => {
                    try {
                      await deleteCustomFolder(folderToDelete.id);
                      if (activeTab === folderToDelete.id) setActiveTab('inbox');
                    } catch (err) {
                      console.error('[EmailFeed] delete folder failed', err);
                    } finally {
                      setShowDeleteFolderConfirm(false);
                      setFolderToDelete(null);
                    }
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {(showEditFolderModal || showDeleteFolderConfirm) && (
        <div className="modal-backdrop fade show" style={{ zIndex: 1090 }}></div>
      )}

      {/* Link to Job Modal */}
      {showLinkModal && linkModalEmail && (
        <div className="modal fade show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1100 }}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '450px' }}>
            <div className="modal-content border-0 shadow-lg">
              <div className="modal-header py-3 border-bottom">
                <h5 className="modal-title fw-bold fs-5">
                  <i className="fas fa-link text-primary me-2"></i>
                  Link Email to Job
                </h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => {
                    setShowLinkModal(false);
                    setLinkModalEmail(null);
                    setLinkModalSelectedCustomer(null);
                    setLinkModalCustomerSearch('');
                  }}
                ></button>
              </div>
              
              <div className="modal-body py-4" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                {/* Email being linked */}
                <div className="bg-light rounded p-3 mb-4">
                  <div className="fw-semibold text-truncate" style={{ maxWidth: '100%' }}>
                    {linkModalEmail.subject || '(No subject)'}
                  </div>
                  <div className="text-muted fs-8">
                    From: {linkModalEmail.from?.[0]?.name || linkModalEmail.from?.[0]?.address || 'Unknown'}
                  </div>
                </div>

                {/* Step 1: Select Customer */}
                {!linkModalSelectedCustomer ? (
                  <>
                    <label className="form-label fw-semibold mb-2">
                      <span className="badge badge-circle badge-primary me-2">1</span>
                      Search Customer
                    </label>
                    <input
                      type="text"
                      className="form-control form-control-solid mb-3"
                      placeholder="Type customer name or email..."
                      value={linkModalCustomerSearch}
                      onChange={(e) => setLinkModalCustomerSearch(e.target.value)}
                      autoFocus
                    />
                    
                    <div className="border rounded" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                      {filteredCustomersForModal.length === 0 ? (
                        <div className="text-muted text-center py-4 fs-8">
                          {linkModalCustomerSearch ? 'No customers found' : 'Start typing to search...'}
                        </div>
                      ) : (
                        filteredCustomersForModal.map((customer) => (
                          <button
                            key={customer.id}
                            className="btn btn-light w-100 text-start py-2 px-3 border-bottom d-flex align-items-center"
                            style={{ borderRadius: 0 }}
                            onClick={() => setLinkModalSelectedCustomer(customer)}
                          >
                            <div className="symbol symbol-30px me-3">
                              <div className="symbol-label bg-light-primary text-primary fw-bold fs-7">
                                {customer.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                              </div>
                            </div>
                            <div className="flex-grow-1" style={{ minWidth: 0 }}>
                              <div className="fw-semibold text-truncate">{customer.name}</div>
                              <div className="text-muted fs-9 text-truncate">{customer.email}</div>
                            </div>
                            <i className="fas fa-chevron-right text-muted fs-9"></i>
                          </button>
                        ))
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    {/* Selected customer header */}
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <div className="d-flex align-items-center">
                        <span className="badge badge-circle badge-success me-2">✓</span>
                        <span className="fw-semibold">{linkModalSelectedCustomer.name}</span>
                      </div>
                      <button 
                        className="btn btn-sm btn-light-primary"
                        onClick={() => setLinkModalSelectedCustomer(null)}
                      >
                        <i className="fas fa-arrow-left fs-9 me-1"></i>
                        Change
                      </button>
                    </div>
                    
                    {/* Step 2: Select Job */}
                    <label className="form-label fw-semibold mb-2">
                      <span className="badge badge-circle badge-primary me-2">2</span>
                      Select Job
                    </label>
                    
                    <div className="border rounded" style={{ maxHeight: '250px', overflowY: 'auto' }}>
                      {jobsForModalCustomer.length === 0 ? (
                        <div className="text-center py-4">
                          <div className="text-muted fs-8 mb-3">No jobs found for this customer</div>
                          <button
                            className="btn btn-sm btn-light-success"
                            onClick={() => {
                              setShowLinkModal(false);
                              openCreateJobModal(linkModalEmail, linkModalSelectedCustomer?.id);
                            }}
                          >
                            <i className="fas fa-plus-circle me-2"></i>
                            Create New Job
                          </button>
                        </div>
                      ) : (
                        jobsForModalCustomer.map((job) => {
                          const isLinking = linkingEmailId === (linkModalEmail.emailId || linkModalEmail.id);
                          const isLinked = linkModalEmail.linkedJobId === job.id;
                          const statusColor = job.status === 'Active' ? 'danger' : 
                                              job.status === 'Open' ? 'primary' : 
                                              job.status === 'Closed' ? 'success' : 'warning';
                          return (
                            <button
                              key={job.id}
                              className={clsx(
                                "btn w-100 text-start py-3 px-3 border-bottom d-flex align-items-center",
                                isLinked ? "bg-light-success" : "btn-light"
                              )}
                              style={{ borderRadius: 0 }}
                              onClick={() => handleLinkToJob(
                                linkModalEmail.emailId || linkModalEmail.id, 
                                job.id, 
                                job.jobLabel
                              )}
                              disabled={isLinking}
                            >
                              <i className={clsx(
                                "fas me-3 fs-5",
                                isLinked ? "fa-check-circle text-success" : "fa-briefcase text-muted"
                              )}></i>
                              <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                <div className="fw-semibold text-truncate">{job.jobLabel}</div>
                              </div>
                              <span className={`badge badge-light-${statusColor} ms-2`}>
                                {job.status}
                              </span>
                              {isLinking && (
                                <span className="ms-2 spinner-border spinner-border-sm" role="status"></span>
                              )}
                            </button>
                          );
                        })
                      )}
                    </div>
                  </>
                )}
              </div>
              
              <div className="modal-footer border-top py-3">
                <button 
                  type="button" 
                  className="btn btn-sm btn-light" 
                  onClick={() => {
                    setShowLinkModal(false);
                    setLinkModalEmail(null);
                    setLinkModalSelectedCustomer(null);
                    setLinkModalCustomerSearch('');
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* New Job Modal - opened from email context */}
      <NewJobModal
        show={showNewJobModal}
        onClose={() => {
          setShowNewJobModal(false);
          setNewJobEmailContext(null);
        }}
        initialCustomerId={newJobEmailContext?.customerId || undefined}
        initialDescription={newJobEmailContext?.email?.subject || undefined}
        onJobCreated={handleNewJobCreated}
      />

      {/* Job Workspace Modal - opens after creating new job from email */}
      {selectedJobForWorkspace && (
        <JobWorkspaceModal
          show={showJobWorkspaceModal}
          job={selectedJobForWorkspace}
          onHide={() => {
            setShowJobWorkspaceModal(false);
            setSelectedJobForWorkspace(null);
          }}
        />
      )}

      </div>
    </div>
  );
};

export default EmailFeedContent;
